import {buildCommentCitationNavigation, citationCommentRef} from '../../../utils/citationNavigation';
import React, {useCallback, useEffect, useMemo, useState, useRef} from 'react';
import {Select, Empty, Button, Alert, Tooltip, Dropdown, message} from 'antd';
import AICommentCard from './AICommentCard';
import AICommentDetailPanel from './AICommentDetailPanel';
import {useDispatch, useSelector} from "react-redux";
import './AICommentCard.css'
import {useTranslation} from "react-i18next";
import {ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, CloseOutlined, CopyOutlined, DownOutlined, DownloadOutlined, Loading3QuartersOutlined, LockOutlined, RobotOutlined, SortAscendingOutlined, WarningOutlined} from '@ant-design/icons';
import {RequestGenerateSummary} from "../../../Actions/stage.action";
import { openCommercialGateModal } from "../../../Actions/commercialControl.action";
import {getCurrentLanguage} from "../../../i18n";
import {BASE_URL} from "../../../config";
import {safeHtml} from "../../../utils/sanitizeHtml";
import { authFetch } from "../../../utils/authFetch";
import { stream_comment_enrichment } from "../../../Services/review.service";
import { IAStreamActionTypes } from "../../../Actions/actionTypes";
import { finishAiTask, startAiTask } from "../../../Reducers/aiTasksReducer";
import LineReveal from "./LineReveal";
import FastGlobalObservations from "./FastGlobalObservations";
import ProviderSummaryPanel from './ProviderSummaryPanel';
import ReferenceReviewCard, {
    filterReferenceReviewCards,
} from "../reference-review/ReferenceReviewCard";
import ReferenceReviewProgressNotice from "../reference-review/ReferenceReviewProgressNotice";
import {getEvidenceTargetRef} from "../reference-review/referenceReviewEvidence";
import {
    projectReferenceReviewCards,
    REFERENCE_REVIEW_CRITERION_NAME,
} from "../reference-review/referenceReviewProjection";
import AutomatedResultsNotice from "./AutomatedResultsNotice";
import { getRecommendationSections } from "../../../utils/recommendationSections";
import {
    getHighlightableCommentRef,
    getComparisonMetadata,
    isPrimaryRiskComment,
    splitPrimaryAndSecondaryComparisonComments,
} from "../../../utils/comparisonRisk";
import {
    buildCommentWorkbookData,
    buildCommentWorkbookLabels,
    downloadCommentWorkbook,
    formatCommentWorkbookDate,
} from './commentWorkbookExport';
import {
    buildLockedCommercialDecision,
    canUseFullCommentDetails,
    canUseStandardExports,
    canUseStrategicMemo,
    getTrialFreeCommentDetailsLimit,
} from "../../../utils/commercialCapabilities";
import {
    buildLegalSignalChipState,
    getLegalSignalCommentTarget,
} from "./legalSignals";
import {
    buildPostAnalysisOffersDecision,
    getPostAnalysisSuccessStorageKey,
    hasSeenPostAnalysisSuccessCard,
    markPostAnalysisSuccessCardSeen,
    shouldShowPostAnalysisSuccessCard,
} from "./postAnalysisSuccess";
import {
    compareReviewSeverity,
    getReviewSeverity,
    normalizeReviewSortText,
} from "../../../utils/reviewDecisionProjection";
import { getGlobalCardPriority, getGlobalPriorityState, isDirectGlobalCard } from '../../../utils/globalCardPriority';


const {Option} = Select;
const MEMO_THINKING_MARKER_RE = /<!--VD_MEMO_THINKING_B64:([A-Za-z0-9+/=]+)-->/g;
const MEMO_THINKING_MARKER_PREFIX = '<!--VD_MEMO_THINKING_B64:';
const SUMMARY_CACHE_TTL_MS = 60 * 60 * 1000;
const DEFAULT_COMMENT_SORT = 'risk';
const SHOW_UNCERTAIN_COMPARISON_CHANGES =
    process.env.REACT_APP_SHOW_UNCERTAIN_COMPARISON_CHANGES !== 'false';
const EMPTY_POV_LIST = Object.freeze([]);
const EMPTY_POV_STATE = Object.freeze({});

export const shouldShowMaterialCoverageDiagnostic = (
    environment = process.env.NODE_ENV,
    hostname = typeof window !== 'undefined' ? window.location.hostname : ''
) => ['development', 'test'].includes(environment)
    && ['localhost', '127.0.0.1', '::1'].includes(String(hostname || '').toLowerCase());

const MATERIAL_COVERAGE_REASON_GROUPS = Object.freeze({
    falseSourceClaim: new Set([
        'source_incompleteness_claim_conflicts_with_complete_sources',
    ]),
    sourceReferenceRejected: new Set([
        'unknown_source_branch',
        'unknown_existing_card_id',
    ]),
    partyAttributionRejected: new Set([
        'unknown_exposed_party',
    ]),
    invalidProviderOutput: new Set([
        'invalid_lean_exposure',
    ]),
});

const isDirectReviewComment = (comment) => {
    const metadata = getComparisonMetadata(comment) || {};
    return [comment?.source_review_method, metadata.source_review_method,
        metadata.fast_global_card_v1?.source_review_method].includes('direct_original_contract');
};

export const getMaterialCoverageDiagnostic = (comments = []) => {
    const incompleteSnapshots = comments
        .filter((comment) => !isDirectReviewComment(comment))
        .map((comment) => getComparisonMetadata(comment) || {})
        .filter((metadata) => metadata.review_mode === 'fast_global'
            && metadata.material_coverage_complete === false);
    if (incompleteSnapshots.length === 0) return null;

    const metadata = incompleteSnapshots.reduce((latest, candidate) => {
        const latestStartedAt = String(latest?.fast_global_run_started_at || '');
        const candidateStartedAt = String(candidate?.fast_global_run_started_at || '');
        return candidateStartedAt > latestStartedAt ? candidate : latest;
    });
    const gaps = Array.isArray(metadata.material_gaps) ? metadata.material_gaps : [];
    const reasons = gaps
        .map((gap) => gap?.reason_code)
        .filter((reason) => typeof reason === 'string' && reason.length > 0);
    const reportedCount = Number(metadata.material_gap_count);
    const count = Number.isFinite(reportedCount) && reportedCount > 0
        ? reportedCount
        : gaps.length;
    if (count === 0) return null;

    let variant = 'coverageGap';
    if (reasons.length > 0) {
        const matchingGroup = Object.entries(MATERIAL_COVERAGE_REASON_GROUPS)
            .find(([, group]) => reasons.every((reason) => group.has(reason)));
        variant = matchingGroup?.[0] || 'mixedValidation';
    }

    return {variant, count};
};

export const getDirectSourceStatus = ({ comments = [], terminal = null, analysisId, isStreaming = false }) => {
    if (isStreaming) return null;
    let status;
    let limitations;
    if (terminal) {
        if (terminal.analysis_id && analysisId && terminal.analysis_id !== analysisId) return null;
        const result = terminal.result || terminal.data?.result || {};
        if (result.source_review_method !== 'direct_original_contract') return null;
        status = terminal.completion_status || terminal.data?.completion_status;
        limitations = result.source_limitations;
    } else {
        // The provided comments already belong to the selected version/POV.
        // Select the latest whole run before inspecting its source limitations.
        const latest = comments.reduce((selected, comment) => {
            const metadata = getComparisonMetadata(comment) || {};
            if (metadata.review_mode !== 'fast_global' && !isDirectReviewComment(comment)) return selected;
            const rank = metadata.fast_global_run_started_at || comment.created_at || '';
            return !selected || rank > selected.rank ? { comment, metadata, rank } : selected;
        }, null);
        if (!latest || !isDirectReviewComment(latest.comment)) return null;
        status = latest.metadata.fast_global_snapshot_status;
        limitations = latest.metadata.source_limitations || latest.metadata.fast_global_card_v1?.source_limitations;
    }
    return {
        status,
        limitations: (Array.isArray(limitations) ? limitations : []).filter((item) => (
            item && ['partial', 'unknown'].includes(item.source_completeness)
        )),
    };
};

export const getCommentRenderKey = (comment = {}) => {
    const metadata = getComparisonMetadata(comment) || {};
    const snapshot = metadata.fast_global_card_v1 || {};
    const method = comment.source_review_method || metadata.source_review_method || snapshot.source_review_method;
    // The persistence endpoint assigns a database UUID. Keep the same React
    // row for the direct risk already being read, using its unique run identity.
    return method === 'direct_original_contract'
        ? metadata.fast_global_card_id || snapshot.card_id || comment.card_id || comment.id
        : comment.id;
};
const buildCommentAiTask = (kind, commentId, scopeId, label) => ({
    id: `${kind}:${commentId}`,
    kind,
    label,
    scopeId: scopeId || commentId,
    blocking: true,
});

const buildSummaryAiTask = (summaryCacheKey, scopeId, label) => ({
    id: `summary:${summaryCacheKey}`,
    kind: 'summary',
    label,
    scopeId: scopeId || summaryCacheKey,
    blocking: true,
});

const normalizeSummaryCacheText = (value) => String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeSortText = normalizeReviewSortText;
const getRiskLevelKey = getReviewSeverity;

const isPriorityRiskComment = (comment) => ['critical', 'high'].includes(getRiskLevelKey(comment?.risk));

export const isAutomatedResultsBannerVisible = ({
    rootRect,
    bannerRect,
    stickyHeaderRect = null,
    clippingRect = null,
} = {}) => {
    if (!rootRect || !bannerRect) return false;

    const visibleTop = Math.max(
        rootRect.top,
        stickyHeaderRect?.bottom ?? rootRect.top,
        clippingRect?.top ?? rootRect.top
    );
    const visibleBottom = Math.min(
        rootRect.bottom,
        clippingRect?.bottom ?? rootRect.bottom
    );

    return bannerRect.bottom > visibleTop && bannerRect.top < visibleBottom;
};

const sortCommentList = (comments = [], sortMode = DEFAULT_COMMENT_SORT) => {
    const indexed = (comments || []).map((comment, index) => ({ comment, index }));

    if (sortMode === 'risk') {
        const globalPriorityState = getGlobalPriorityState(comments);
        const globalCards = comments.filter(isDirectGlobalCard);
        const ordinaryCards = indexed.filter(({ comment }) => !isDirectGlobalCard(comment))
            .sort((a, b) => {
                const riskDelta = compareReviewSeverity(a.comment?.risk, b.comment?.risk);
                return riskDelta || a.index - b.index;
            })
            .map(({ comment }) => comment);
        const rankedGlobalCards = globalPriorityState === 'final'
            ? [...globalCards].sort((a, b) => getGlobalCardPriority(a).rank - getGlobalCardPriority(b).rank)
            : globalCards;
        return [...rankedGlobalCards, ...ordinaryCards];
    }

    if (sortMode === 'criterion') {
        return indexed
            .sort((a, b) => {
                const criterionDelta = normalizeSortText(a.comment?.criterion_title)
                    .localeCompare(normalizeSortText(b.comment?.criterion_title));
                return criterionDelta || a.index - b.index;
            })
            .map(({ comment }) => comment);
    }

    return comments || [];
};

const getMatchedCriterionTitles = (comment = {}) => {
    let metadata = comment?.metadata_json ?? comment?.metadataJson ?? {};
    if (typeof metadata === 'string') {
        try {
            metadata = JSON.parse(metadata);
        } catch (_) {
            metadata = {};
        }
    }
    const snapshot = metadata?.fast_global_card_v1 || {};
    const matched = comment?.matched_criteria
        || metadata?.matched_criteria
        || snapshot?.matched_criteria
        || [];
    return Array.from(new Set(
        (Array.isArray(matched) ? matched : [])
            .map(item => String(item?.title || '').trim())
            .filter(Boolean)
    ));
};

const commentMatchesCriterion = (comment, selectedCriterion) => (
    comment?.criterion_title === selectedCriterion
    || getMatchedCriterionTitles(comment).includes(selectedCriterion)
);

export const isFastGlobalToConfirmComment = (comment = {}) => {
    // Confidence remains on the direct global card; it never lowers its rank.
    if (isDirectGlobalCard(comment)) return false;
    let metadata = comment?.metadata_json ?? comment?.metadataJson ?? {};
    if (typeof metadata === 'string') {
        try { metadata = JSON.parse(metadata); } catch (_) { metadata = {}; }
    }
    const annotations = metadata?.review_annotations
        || metadata?.fast_global_card_v1?.review_annotations
        || [];
    const finalForRun = metadata?.final_for_run === true
        || metadata?.fast_global_card_v1?.final_for_run === true;
    if (finalForRun) return false;
    return Array.isArray(annotations)
        && annotations.some((value) => ['to_confirm', 'party_ambiguous', 'source_partial'].includes(value));
};

export const buildSidebarCommentGroups = ({
    comments = [],
    selectedCriterion = null,
    sortMode = DEFAULT_COMMENT_SORT,
    language = 'fr',
} = {}) => {
    const scoped = selectedCriterion
        ? (comments || []).filter(c => commentMatchesCriterion(c, selectedCriterion))
        : (comments || []);
    const { primary, comparisonChanges, uncertainComparisonChanges } = splitPrimaryAndSecondaryComparisonComments(scoped, language);
    return {
        primary: sortCommentList(primary, sortMode),
        comparisonChanges: sortCommentList(comparisonChanges, sortMode),
        uncertainComparisonChanges: sortCommentList(uncertainComparisonChanges, sortMode),
        scoped,
    };
};

const hashSummaryCacheInput = (value) => {
    let hash = 2166136261;
    const text = String(value ?? '');
    for (let i = 0; i < text.length; i += 1) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
};

const buildSummaryCommentsPayload = (comments = []) => comments.map(c => ({
    id: c.id,
    criterion_title: c.criterion_title,
    description: c.quick_view?.description || c.description,
    quick_view: c.quick_view,
    detailed_view: c.detailed_view,
    risk: c.risk,
    quote: c.quote
}));

const buildSummaryCacheKey = ({commentsPayload, documentVersionId, language}) => {
    const stableComments = (commentsPayload || []).map((comment, index) => ({
        key: normalizeSummaryCacheText(comment.id || comment.doc_ref || comment.ref || `${comment.criterion_title || ''}:${index}`),
        id: normalizeSummaryCacheText(comment.id),
        criterion_title: normalizeSummaryCacheText(comment.criterion_title),
        description: normalizeSummaryCacheText(comment.description),
        quick_view: normalizeSummaryCacheText(JSON.stringify(comment.quick_view || {})),
        detailed_view: normalizeSummaryCacheText(JSON.stringify(comment.detailed_view || {})),
        risk: normalizeSummaryCacheText(comment.risk),
        quote: normalizeSummaryCacheText(comment.quote),
    })).sort((a, b) => a.key.localeCompare(b.key));
    const fingerprint = hashSummaryCacheInput(JSON.stringify(stableComments));
    return [
        'ai-summary-v1',
        normalizeSummaryCacheText(documentVersionId || 'no-document'),
        normalizeSummaryCacheText(language || 'default'),
        stableComments.length,
        fingerprint,
    ].join(':');
};

const isUsableSummaryCacheEntry = (entry) => {
    if (!entry || !entry.html || !entry.createdAt) return false;
    const createdAt = Date.parse(entry.createdAt);
    return Number.isFinite(createdAt) && Date.now() - createdAt < SUMMARY_CACHE_TTL_MS;
};

export const isStrategicMemoCacheAvailable = ({
    filteredCommentsCount = 0,
    cacheEntry,
    strategicMemoAllowed = false,
} = {}) => (
    Boolean(strategicMemoAllowed)
    && filteredCommentsCount > 0
    && isUsableSummaryCacheEntry(cacheEntry)
    && cacheEntry.status === 'done'
);

export const scrollStrategicMemoIntoView = (sidebarEl) => {
    if (!sidebarEl) return false;
    const summaryEl = sidebarEl.querySelector(".ai-summary-brief");
    if (summaryEl && typeof summaryEl.scrollIntoView === "function") {
        summaryEl.scrollIntoView({ behavior: "auto", block: "start" });
        return true;
    }
    if (typeof sidebarEl.scrollTo === "function") {
        sidebarEl.scrollTo({ top: 0, behavior: "auto" });
        return true;
    }
    sidebarEl.scrollTop = 0;
    return true;
};

const escapeSummaryHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const stripDanglingSummaryHeading = (html) => {
    let next = String(html ?? '').trimEnd();
    let previous;

    do {
        previous = next;
        next = next
            .replace(/\s*<p>\s*<strong>[^<]{1,120}<\/strong>\s*<\/p>\s*$/i, '')
            .trimEnd();
    } while (next !== previous);

    return next;
};

const normalizeSummaryHtmlPayload = (raw) => {
    let text = String(raw ?? '').trim();
    if (!text) return '';

    text = text
        .replace(/^```(?:html)?\s*/i, '')
        .replace(/\s*```$/i, '')
        .trim();

    if (/<\/?[a-z][\s\S]*>/i.test(text)) {
        return stripDanglingSummaryHeading(text);
    }

    return stripDanglingSummaryHeading(text
        .split(/\n{2,}/)
        .map((block) => block.trim())
        .filter(Boolean)
        .map((block) => {
            const normalizedBlock = block.replace(/\n+/g, '<br />');
            const plainHeading = !/[.!?:;]$/.test(block) && block.length <= 80;
            if (plainHeading && !block.includes('<br />')) {
                return `<p><strong>${escapeSummaryHtml(block)}</strong></p>`;
            }
            return `<p>${escapeSummaryHtml(normalizedBlock).replace(/&lt;br \/&gt;/g, '<br />')}</p>`;
        })
        .join('\n'));
};

const decodeMemoThinkingChunk = (encoded) => {
    try {
        const binary = atob(encoded);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        return new TextDecoder('utf-8').decode(bytes);
    } catch (e) {
        return '';
    }
};

const splitMemoStreamPayload = (rawText = '') => {
    const thinkingMessages = [];
    const content = rawText.replace(MEMO_THINKING_MARKER_RE, (_, encoded) => {
        const decoded = decodeMemoThinkingChunk(encoded);
        if (decoded.trim()) {
            thinkingMessages.push(decoded.trim());
        }
        return '';
    });
    const partialIdx = content.lastIndexOf(MEMO_THINKING_MARKER_PREFIX);
    return {
        content: partialIdx >= 0 ? content.slice(0, partialIdx) : content,
        thinkingMessages,
    };
};

const stripFastGlobalAuxiliarySections = (markdown = '') => {
    const raw = String(markdown);
    const auxiliaryMatch = /(?:prochaine vérification|next verification|sources contractuelles|contractual sources)/i.exec(raw);
    if (auxiliaryMatch) {
        const precedingHtmlBlock = raw.lastIndexOf('<p', auxiliaryMatch.index);
        const precedingLine = raw.lastIndexOf('\n', auxiliaryMatch.index);
        const sectionStart = Math.max(precedingHtmlBlock, precedingLine + 1);
        return raw.slice(0, sectionStart >= 0 ? sectionStart : auxiliaryMatch.index).trim();
    }

    let skipping = false;
    return raw.split('\n').filter((line) => {
        const heading = line.match(/^\s*(?:#{1,6}\s+(.+?)|\*\*(.+?)\*\*)\s*$/);
        if (heading) {
            const label = String(heading[1] || heading[2] || '').trim().toLocaleLowerCase();
            const auxiliary = label === 'prochaine vérification'
                || label === 'next verification'
                || label === 'sources contractuelles'
                || label === 'contractual sources';
            if (auxiliary) {
                skipping = true;
                return false;
            }
            skipping = false;
        }
        return !skipping;
    }).join('\n').trim();
};

const AICommentSidebar = ({
                              comments, currentPage,
                              selectedRef,
                              selectedCommentId,
                              commentFocusRequest = null,
                              onCommentHover, onCommentClick, showAll,
                              onVisibleCommentsChange,
                              analysisId,
                              documentVersionId,
                              onStrategicMemoOpen,
                              onReferenceEvidenceClick,
                              onVersionEvidenceClick,
                              onAutomatedResultsBannerVisibilityChange,
                              focusStrategicMemoOnShow = false,
                              hasCompleteAnalysisPov = false,
                              povTransitioning = false,
                              povResultsError = null,
                              onRetryPovResults,
                              onPovRequired,
                          }) => {
    const sidebarRef = useRef(null);
    const automatedResultsBannerRef = useRef(null);
    const [displayed, setDisplayed] = useState(showAll ? comments : comments?.filter(c => c.page === currentPage));
    const [expandedCommentId, setExpandedCommentId] = useState(null);

    useEffect(() => {
        setExpandedCommentId(null);
    }, [analysisId, documentVersionId]);

    // Selection opens a card; changing the streamed content never reopens it.
    useEffect(() => {
        if (selectedCommentId != null) setExpandedCommentId(selectedCommentId);
    }, [selectedCommentId]);

    // A fresh document click can target the same already-selected comment.
    useEffect(() => {
        if (commentFocusRequest?.commentId != null) {
            setExpandedCommentId(commentFocusRequest.commentId);
        }
    }, [commentFocusRequest?.commentId, commentFocusRequest?.requestId]);
    const [exitingIds, setExitingIds] = useState(new Set());
    const [selectedCriterion, setSelectedCriterion] = useState(null);
    const [sortMode, setSortMode] = useState(DEFAULT_COMMENT_SORT);
    const [moreStateById, setMoreStateById] = useState({});
    const [fastGlobalAnalysisById, setFastGlobalAnalysisById] = useState({});
    const [isExporting, setIsExporting] = useState(false);
    const [hideMaterialCoverageWarning, setHideMaterialCoverageWarning] = useState(false);
    const [reasoningHidden, setReasoningHidden] = useState(false);
    const [reasoningFallbackIndex, setReasoningFallbackIndex] = useState(0);
    const [reasoningCollapsed, setReasoningCollapsed] = useState(false);
    const [reasoningAutoScroll, setReasoningAutoScroll] = useState(true);
    const reasoningFeedScrollRef = useRef(null);
    // Ref utilisé par l'effet d'auto-collapse pour lire la valeur courante
    // sans la mettre dans les dépendances (évite la boucle collapse→reopen).
    const reasoningCollapsedRef = useRef(reasoningCollapsed);
    // Sync du ref à chaque rendu
    reasoningCollapsedRef.current = reasoningCollapsed;
    // ── Auto-collapse one-shot par session d'analyse ──────────────────────────
    // On ne replie automatiquement le gadget de raisonnement qu'UNE seule fois
    // (à l'arrivée du premier commentaire). Sans ce flag, chaque nouveau
    // commentaire qui arrive re-déclencherait setReasoningCollapsed(true) et
    // refermerait le panneau que l'utilisateur vient de rouvrir manuellement.
    // Ce ref est remis à false au démarrage d'un nouveau stream (cf. effet
    // BUG FIX #1 ci-dessous).
    const hasAutoCollapsedRef = useRef(false);
    const previousCommentsCountRef = useRef(comments?.length || 0);

    // ── PR#3.4.1 : enrichissement stocké dans Redux (comment.enrichment) ─────
    // Plus de state local enrichmentById — source de vérité unique dans ia_comments.
    // Seuls les AbortControllers restent en ref locale (pas de sérialisation Redux nécessaire).
    const enrichmentAbortControllersRef = useRef({});
    const ENRICHMENT_TIMEOUT_MS = 120_000;

    // Cleanup : annuler tous les streams en cours lors du démontage du composant
    useEffect(() => {
        const controllers = enrichmentAbortControllersRef.current;
        return () => {
            Object.values(controllers).forEach((ctrl) => {
                try {
                    ctrl.abort(); // sans raison custom (portabilité cross-browser)
                } catch (_) {}
            });
        };
    }, []);

    // États pour gérer le hover et la sélection spécifique de chaque carte
    const [hoveredCommentId, setHoveredCommentId] = useState(null);

    // États pour le panel détaillé
    const [detailPanelVisible, setDetailPanelVisible] = useState(false);
    const [selectedCommentForDetail, setSelectedCommentForDetail] = useState(null);

    // États pour le résumé IA
    const [summaryVisible, setSummaryVisible] = useState(false);
    const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
    const [aiSummary, setAiSummary] = useState('');
    const [summaryError, setSummaryError] = useState(null);
    const [summaryCopied, setSummaryCopied] = useState(false);
    const [comparisonChangesExpanded, setComparisonChangesExpanded] = useState(true);
    const [uncertainComparisonChangesExpanded, setUncertainComparisonChangesExpanded] = useState(true);
    const [visibleSummaryCacheKey, setVisibleSummaryCacheKey] = useState(null);
    const [postAnalysisSuccessVisible, setPostAnalysisSuccessVisible] = useState(false);
    const riskListScrollTopRef = useRef(0);
    const summaryBackButtonRef = useRef(null);
    const previousSummaryCacheKeyRef = useRef(null);

    const povResultsVisible = hasCompleteAnalysisPov && !povTransitioning;
    const ia_comments_loading = useSelector((state) => state.documentVersion.ia_comments_loading);
    const storedIaStreaming = useSelector((state) => state.documentVersion.ia_streaming);
    const ia_streaming = povResultsVisible ? storedIaStreaming : false;
    const summaryCache = useSelector((state) => state.documentVersion.summary_cache || {});
    const storedAnalysisReasoningMessages = useSelector((state) => state.documentVersion.analysis_reasoning_messages || []);
    const storedAnalysisReasoningText = useSelector((state) => state.documentVersion.analysis_reasoning_text || '');
    const storedAnalysisReasoningStatus = useSelector((state) => state.documentVersion.analysis_reasoning_status || 'idle');
    const storedAnalysisStreamMode = useSelector((state) => state.documentVersion.analysis_stream_mode || null);
    const analysisReasoningMessages = povResultsVisible ? storedAnalysisReasoningMessages : EMPTY_POV_LIST;
    const analysisReasoningText = povResultsVisible ? storedAnalysisReasoningText : '';
    const analysisReasoningStatus = povResultsVisible ? storedAnalysisReasoningStatus : 'idle';
    const storedProviderSummary = useSelector((state) => state.documentVersion.provider_summary || null);
    const providerSummary = povResultsVisible ? storedProviderSummary : null;
    const hasProviderSummary = Boolean(providerSummary?.text?.trim() || providerSummary?.batch_activity);
    const analysisStreamMode = povResultsVisible ? storedAnalysisStreamMode : null;
    const storedReferenceReview = useSelector((state) => state.documentVersion.reference_review || {});
    const referenceReview = useMemo(() => (
        hasCompleteAnalysisPov && !povTransitioning
            ? storedReferenceReview
            : {}
    ), [hasCompleteAnalysisPov, povTransitioning, storedReferenceReview]);
    const storedFastGlobalAnalysisStarted = useSelector((state) => state.documentVersion.fast_global_analysis_started || false);
    const storedFastGlobalObservations = useSelector((state) => state.documentVersion.fast_global_observations || []);
    const storedFastGlobalCriteriaStatus = useSelector((state) => state.documentVersion.fast_global_criteria_status || {});
    const storedFastGlobalTerminal = useSelector((state) => state.documentVersion.fast_global_terminal || null);
    const storedFastGlobalThinkingItems = useSelector((state) => state.documentVersion.fast_global_thinking_items || []);
    const storedFastGlobalCardsStarted = useSelector((state) => state.documentVersion.fast_global_cards_started || false);
    const fastGlobalAnalysisStarted = povResultsVisible ? storedFastGlobalAnalysisStarted : false;
    const fastGlobalObservations = povResultsVisible ? storedFastGlobalObservations : EMPTY_POV_LIST;
    const fastGlobalCriteriaStatus = povResultsVisible ? storedFastGlobalCriteriaStatus : EMPTY_POV_STATE;
    const fastGlobalTerminal = povResultsVisible ? storedFastGlobalTerminal : null;
    const fastGlobalThinkingItems = povResultsVisible ? storedFastGlobalThinkingItems : EMPTY_POV_LIST;
    const fastGlobalCardsStarted = povResultsVisible ? storedFastGlobalCardsStarted : false;
    const structuredProgressVisible = ['fast_global', 'comparison'].includes(analysisStreamMode);
    const isPageLoading = useSelector((state) => state.documentVersion?.isLoading);
    const isGlobalLoading = useSelector((state) => state.stage.isGlobalLoading);
    const labelsVariant = useSelector((state) => state.stage?.labels_variant || 'default_v1');
    const commercialCapabilities = useSelector((state) => state.commercialControl?.capabilities || {});
    const currentUser = useSelector((state) => {
        const usersState = state.users || {};
        return usersState.user?.id ? usersState.user : usersState.currentUser || {};
    });
    const storeDocumentVersionId = useSelector(
        (state) => state.documentVersion?.content?.id || state.documentVersion?.currentVersion
    );
    useEffect(() => {
    }, [analysisId, documentVersionId, storeDocumentVersionId]);
    const referenceReviewCards = useMemo(
        () => {
            const currentVerdictCount = Object.keys(referenceReview?.verdicts_by_id || {}).length;
            const freshReferenceStreamWaitingForVerdicts = analysisStreamMode === 'reference_review'
                && ia_streaming
                && referenceReview?.result_source === 'stream'
                && currentVerdictCount === 0;
            return freshReferenceStreamWaitingForVerdicts
                ? []
                : projectReferenceReviewCards(referenceReview);
        },
        [analysisStreamMode, ia_streaming, referenceReview]
    );
    const referenceReviewCriterionName = referenceReviewCards[0]?.criterion_name
        || referenceReviewCards[0]?.criterion_title
        || referenceReview?.context?.criterion_name
        || REFERENCE_REVIEW_CRITERION_NAME;
    const hasReferenceReviewState = referenceReviewCards.length > 0
        || (referenceReview?.requirement_order || []).length > 0
        || (analysisStreamMode === 'reference_review' && ia_streaming)
        || referenceReview?.status === 'failed'
        || ['absent', 'error', 'incompatible'].includes(referenceReview?.hydration_status);
    const criterionOptions = useMemo(() => Array.from(new Set(
        [
            ...(comments || []).flatMap(comment => [
                comment?.criterion_title,
                ...getMatchedCriterionTitles(comment),
            ]),
            ...referenceReviewCards.map(card => card?.criterion_name || card?.criterion_title),
            hasReferenceReviewState ? referenceReviewCriterionName : null,
        ].filter(Boolean)
    )), [comments, hasReferenceReviewState, referenceReviewCards, referenceReviewCriterionName]);

    const dispatch = useDispatch();

    useEffect(() => {
        if (selectedCriterion && !criterionOptions.includes(selectedCriterion)) {
            setSelectedCriterion(null);
        }
    }, [criterionOptions, selectedCriterion]);

    useEffect(() => () => {
        dispatch({ type: IAStreamActionTypes.CLEAR_FAST_GLOBAL_OBSERVATIONS });
    }, [analysisId, dispatch]);

    const {t: tReview} = useTranslation("review");
    const strategicMemoAllowed = canUseStrategicMemo(commercialCapabilities);
    const standardExportsAllowed = canUseStandardExports(commercialCapabilities);
    const fullCommentDetailsAllowed = canUseFullCommentDetails(commercialCapabilities);
    const trialFreeCommentDetailsLimit = getTrialFreeCommentDetailsLimit(commercialCapabilities);
    const lockedFeatureTooltip = tReview("commercial.lock.availableWith", {
        defaultValue: "Disponible avec Solo / Pro",
    });
    const openLockedFeature = useCallback((featureKey, overrides = {}) => {
        dispatch(openCommercialGateModal(buildLockedCommercialDecision(featureKey, overrides)));
    }, [dispatch]);
    const currentUserId = currentUser?.id || currentUser?.user_id || currentUser?.uuid || "";
    const currentUserClientId = currentUser?.client_id
        || currentUser?.clientId
        || currentUser?.workspace_id
        || currentUser?.client?.id
        || currentUser?.client?.uuid
        || "";
    const postAnalysisSuccessStorageKey = useMemo(
        () => getPostAnalysisSuccessStorageKey({
            id: currentUserId,
            client_id: currentUserClientId,
        }),
        [currentUserClientId, currentUserId]
    );
    const openPostAnalysisOffers = useCallback(() => {
        dispatch(openCommercialGateModal(buildPostAnalysisOffersDecision()));
        setPostAnalysisSuccessVisible(false);
    }, [dispatch]);
    const reasoningFallbackRotation = useMemo(() => ([
        tReview('sidebar.reasoningFallbacks.readingClauses', 'Reading clauses...'),
        tReview('sidebar.reasoningFallbacks.comparingClauses', 'Comparing related clauses...'),
        tReview('sidebar.reasoningFallbacks.verifyingSources', 'Verifying source passages...'),
        tReview('sidebar.reasoningFallbacks.checkingConsistency', 'Checking contract consistency...'),
    ]), [tReview]);
    const commentSortOptions = useMemo(() => ([
        {
            key: 'appearance',
            label: tReview('sidebar.sort.appearance'),
        },
        {
            key: 'risk',
            label: tReview('sidebar.sort.risk'),
        },
        {
            key: 'criterion',
            label: tReview('sidebar.sort.criterion'),
        },
    ]), [tReview]);
    const commentSortMenuItems = useMemo(() => commentSortOptions.map((option) => ({
        key: option.key,
        label: (
            <span className="comments-sort-menu-item">
                <span>{option.label}</span>
                {sortMode === option.key && <CheckOutlined className="comments-sort-menu-check" />}
            </span>
        ),
    })), [commentSortOptions, sortMode]);


    useEffect(() => {
        const next = showAll
            ? comments
            : comments.filter(c => c.page === currentPage);

        const toExit = displayed
            .filter(c => !next?.some(n => getCommentRenderKey(n) === getCommentRenderKey(c)))
            ?.map(c => c.id);

        if (toExit.length) {
            setExitingIds(prev => new Set([...prev, ...toExit]));
            const t = setTimeout(() => {
                setDisplayed(next);
                setExitingIds(new Set());
            }, 300);
            return () => clearTimeout(t);
        } else {
            setDisplayed(next);
        }
    }, [comments, currentPage, showAll]);

    useEffect(() => {
        if (!sidebarRef.current || (!selectedRef && !selectedCommentId)) return;
        const selector = selectedCommentId
            ? `[data-comment-id="${selectedCommentId}"]`
            : `[data-ref="${selectedRef}"]`;
        const el = sidebarRef.current.querySelector(selector);
        el?.scrollIntoView({behavior: 'smooth', block: 'center'});
    }, [selectedRef, selectedCommentId, commentFocusRequest?.requestId]);

    useEffect(() => {
        if (!comments?.length) return;

        setMoreStateById((prev) => {
            const next = { ...prev };
            for (const c of comments) {
                // si backend dit done, on force local done (ou au moins on évite running)
                if (c.more_status === "done" && c.more_content) {
                    // ne pas écraser un running actif, mais sur refresh ça n'existe pas
                    if (!next[c.id] || next[c.id].status !== "running") {
                        next[c.id] = { status: "done", content: c.more_content };
                    }
                }
            }
            return next;
        });
        // Note PR#3.4.1 : le seed de comment.enrichment depuis detailed_view initial
        // est maintenant fait dans le reducer (normalizeCommentEnrichment).
        // Pas de setEnrichmentById ici.
    }, [comments]);

    useEffect(() => {
        if (analysisReasoningStatus === 'active' || analysisReasoningMessages.length > 0) {
            setReasoningHidden(false);
            setDetailPanelVisible(false);
            setSelectedCommentForDetail(null);
        }
        // No auto-hide on completion: user can read or collapse manually.
    }, [analysisReasoningMessages.length, analysisReasoningStatus]);

    // ── BUG FIX #1 : ré-ouvrir la barre de raisonnement au démarrage de chaque
    // nouvelle analyse. Sans ce reset, la barre reste repliée (état hérité de la
    // session précédente) alors que du contenu arrive déjà.
    const prevStreamingRef = useRef(ia_streaming);
    useEffect(() => {
        if (!prevStreamingRef.current && ia_streaming) {
            // Le stream vient de démarrer → dérouler la barre
            setReasoningCollapsed(false);
            setReasoningHidden(false);
            setDetailPanelVisible(false);
            setSelectedCommentForDetail(null);
            // Reset du flag one-shot : on autorisera un nouveau auto-collapse
            // dès l'arrivée du 1er commentaire de cette nouvelle session.
            hasAutoCollapsedRef.current = false;
        }
        prevStreamingRef.current = ia_streaming;
    }, [ia_streaming]);

    useEffect(() => {
        if (!ia_streaming || analysisReasoningStatus === 'completed') return undefined;
        const interval = setInterval(() => {
            setReasoningFallbackIndex((prev) => (prev + 1) % reasoningFallbackRotation.length);
        }, 2600);
        return () => clearInterval(interval);
    }, [ia_streaming, analysisReasoningStatus, reasoningFallbackRotation.length]);

    // Graceful handoff: collapse le raisonnement au premier commentaire de la
    // session, puis laisser l'utilisateur le rouvrir sans re-collapse ensuite.
    useEffect(() => {
        const previousCount = previousCommentsCountRef.current;
        const nextCount = comments?.length || 0;
        previousCommentsCountRef.current = nextCount;

        if (nextCount === 0) {
            hasAutoCollapsedRef.current = false;
            return;
        }

        const commentArrived = nextCount > previousCount;
        const reasoningIsVisibleForSession =
            ia_streaming ||
            analysisReasoningStatus === 'active' ||
            analysisReasoningText.length > 0 ||
            analysisReasoningMessages.length > 0;

        if (
            !commentArrived ||
            !reasoningIsVisibleForSession ||
            hasAutoCollapsedRef.current
        ) {
            return;
        }

        hasAutoCollapsedRef.current = true;
        if (!reasoningCollapsedRef.current) {
            setReasoningCollapsed(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        comments?.length,
        ia_streaming,
        analysisReasoningMessages.length,
        analysisReasoningStatus,
        analysisReasoningText.length,
    ]); // reasoningCollapsed exclu volontairement

    // (auto-scroll effect moved below — needs renderedReasoningText which is declared later)

    // Écouter les réinitialisations de critère (dispatchées depuis le panneau de critères)
    useEffect(() => {
        const handler = (e) => {
            try {
                const title = e?.detail?.criterionTitle;
                // Si le filtre courant correspond au critère réinitialisé, on le nettoie
                if (title && selectedCriterion === title) {
                    setSelectedCriterion(null);
                }
            } catch (err) {
                // ignore
            }
        };
        window.addEventListener('criterionReset', handler);
        return () => window.removeEventListener('criterionReset', handler);
    }, [selectedCriterion]);

    // Écouter un flush global de l'analyse pour vider le filtre des commentaires
    useEffect(() => {
        const handler = () => {
            setSelectedCriterion(null);
            setDetailPanelVisible(false);
            setSelectedCommentForDetail(null);
            setMoreStateById({});
            setHoveredCommentId(null);
            setSummaryVisible(false);
            setSummaryError(null);
            setSummaryCopied(false);
            setVisibleSummaryCacheKey(null);
            setComparisonChangesExpanded(false);
            setUncertainComparisonChangesExpanded(false);
        };
        window.addEventListener('analysisFlushed', handler);
        return () => window.removeEventListener('analysisFlushed', handler);
    }, []);

    useEffect(() => {
        if (!detailPanelVisible || !selectedCommentForDetail?.id) return;
        const stillExists = comments?.some((comment) => comment.id === selectedCommentForDetail.id);
        if (!stillExists) {
            setDetailPanelVisible(false);
            setSelectedCommentForDetail(null);
        }
    }, [comments, detailPanelVisible, selectedCommentForDetail?.id]);
    // Handler pour le hover spécifique des cartes
    const handleCardHover = (commentId, ref) => {
        setHoveredCommentId(commentId);
        if (ref !== selectedRef) onCommentHover(ref);
    };

    // fonction utilitaire: construire le texte du commentaire à envoyer
    const buildCommentText = (c) => {
        const parts = [];
        if (c?.title) parts.push(String(c.title));
        if (c?.quick_view?.description) parts.push(String(c.quick_view.description));
        else if (c?.description) parts.push(String(c.description));
        if (c?.detailed_view?.problem) parts.push(String(c.detailed_view.problem));
        if (c?.detailed_view?.impact) parts.push(String(c.detailed_view.impact));
        if (c?.quote) parts.push(`Quote: ${String(c.quote)}`);
        return parts.join("\n\n").trim();
    };

    const getPlainSummaryText = (html = '') => {
        if (!html) return '';
        try {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return doc.body.textContent?.replace(/\n{3,}/g, '\n\n').trim() || '';
        } catch (e) {
            return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        }
    };

    const copySummaryToClipboard = async () => {
        const text = getPlainSummaryText(aiSummary);
        if (!text) return;

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }

            setSummaryCopied(true);
            setTimeout(() => setSummaryCopied(false), 1800);
        } catch (e) {
            setSummaryCopied(false);
        }
    };

    // fonction utilitaire pour stream "En savoir plus" (More) depuis le backend en mode POST + body minimal
    // Backend attend maintenant un body JSON: { comment, reference_text (opt), language, max_completion_tokens (opt), contract_title (opt) }
    const streamMoreForComment = async ({ documentVersionId, aiCommentId, onChunk, commentObj, force = false }) => {
        const requestId = typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`;

        const language = getCurrentLanguage();
        const url = `${BASE_URL}ia-review-results/${documentVersionId}/ai-comment-details/${aiCommentId}/stream${force ? '?force=true' : ''}`;
        const abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

        // Le token est injecté automatiquement par authFetch si disponible

        // Note: reference_text optionnel — nous n'avons pas le texte du fichier de référence côté FE, donc on envoie null
        const payload = {
            comment: buildCommentText(commentObj) || commentObj?.title || String(aiCommentId),
            reference_text: null,
            language,
        };

        const res = await authFetch(url, {
            method: "POST",
            headers: {
                "Accept": "text/plain",
                "Content-Type": "application/json",
                "X-Request-Id": requestId,
                // Authorization: injectée automatiquement par authFetch
            },
            body: JSON.stringify(payload),
            ...(abortController ? { signal: abortController.signal } : {}),
        });

        if (res.status === 409) {
            const err = new Error("already_running");
            err.code = 409;
            throw err;
        }
        if (!res.ok || !res.body) {
            throw new Error(`more_stream_failed_${res.status}`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder("utf-8");

        let raw = "";

        while (true) {
            let idleTimer = null;
            const idleTimeout = new Promise((_, reject) => {
                idleTimer = setTimeout(() => {
                    abortController?.abort();
                    reader.cancel?.().catch?.(() => {});
                    const error = new Error('provider_timeout');
                    error.code = 'provider_timeout';
                    error.retryable = true;
                    reject(error);
                }, 20000);
            });
            let result;
            try {
                result = await Promise.race([reader.read(), idleTimeout]);
            } finally {
                if (idleTimer) clearTimeout(idleTimer);
            }
            const { value, done } = result;
            if (done) break;
            const chunk = decoder.decode(value, { stream: true });

            if (chunk) {
                raw += chunk;
                const parsed = splitMemoStreamPayload(raw);
                onChunk && onChunk({
                    content: parsed.content,
                    reasoning: parsed.thinkingMessages.join('\n'),
                });
            }
        }

        return splitMemoStreamPayload(raw).content;
    };

    const handleCardLeave = () => {
        setHoveredCommentId(null);
        onCommentHover(null);
    };

    // Handler pour le clic spécifique des cartes
    const handleCardClick = (commentId, ref, citationNavigation = null) => {
        if (citationNavigation) onCommentClick(ref || null, commentId, citationNavigation);
        else onCommentClick(ref || null, commentId);

        if (citationNavigation) return;

        if (!ref) return;
        const event = new CustomEvent("force-scroll", { detail: ref });
        window.dispatchEvent(event);
    };

    // Handler pour "En savoir plus"
    const handleMoreClick = async (comment, { force = false } = {}) => {
        const metadata = getComparisonMetadata(comment);
        if (metadata?.review_mode === 'fast_global') {
            if (ia_streaming || metadata?.snapshot_persisted === false) return;
            const current = fastGlobalAnalysisById[comment.id];
            if (!fullCommentDetailsAllowed) {
                openLockedFeature('comment_details', { metadata_json: { reason: 'full_comment_details_locked_or_unknown' } });
                return;
            }
            const documentVersionId = comment?.document_version_id || comment?.documentVersionId || storeDocumentVersionId;
            if (!documentVersionId) return;
            const primaryEvidence = Array.isArray(metadata?.evidence_locations)
                ? metadata.evidence_locations.find((item) => item && typeof item === 'object')
                : null;
            const sourceRef = primaryEvidence?.original_source_id
                || primaryEvidence?.target_block_id
                || getHighlightableCommentRef(comment);
            if (sourceRef) handleCardClick(comment.id, sourceRef);
            setSelectedCommentForDetail(comment);
            setDetailPanelVisible(true);
            if (!force && (current?.status === 'running' || current?.status === 'done')) return;
            setFastGlobalAnalysisById((prev) => ({ ...prev, [comment.id]: { status: 'running', content: '' } }));
            try {
                const finalContent = await streamMoreForComment({
                    documentVersionId,
                    aiCommentId: comment.id,
                    commentObj: comment,
                    force: force || current?.status === 'error',
                    onChunk: ({ content }) => setFastGlobalAnalysisById((prev) => ({
                        ...prev,
                        [comment.id]: {
                            ...(prev[comment.id] || {}),
                            status: 'running',
                            content: stripFastGlobalAuxiliarySections(content),
                        },
                    })),
                });
                setFastGlobalAnalysisById((prev) => ({
                    ...prev,
                    [comment.id]: {
                        ...(prev[comment.id] || {}),
                        status: 'done',
                        content: stripFastGlobalAuxiliarySections(finalContent),
                    },
                }));
            } catch (error) {
                setFastGlobalAnalysisById((prev) => ({
                    ...prev,
                    [comment.id]: {
                        ...(prev[comment.id] || {}),
                        status: 'error',
                        errorCode: error?.code || error?.status || 'stream_unavailable',
                        retryable: error?.retryable !== false,
                        requestId: error?.requestId || null,
                    },
                }));
            }
            return;
        }
        const local = moreStateById[comment.id];

        // 0) Si déjà en cache local => open direct + done
        if (local?.content && local.status === "done") {
            setSelectedCommentForDetail(comment);
            setDetailPanelVisible(true);
            return;
        }

        // 0bis) Si backend dit déjà done (liste fraîche) => open direct + sync local en done
        if (comment.more_status === "done" && comment.more_content) {
            setMoreStateById((prev) => ({
                ...prev,
                [comment.id]: { status: "done", content: comment.more_content, reasoning: "" },
            }));
            setSelectedCommentForDetail(comment);
            setDetailPanelVisible(true);
            return;
        }

        if (!fullCommentDetailsAllowed) {
            openLockedFeature("comment_details", {
                metadata_json: {
                    trial_free_comment_details_limit: trialFreeCommentDetailsLimit,
                    reason: "full_comment_details_locked_or_unknown",
                },
            });
            return;
        }

        // 1) Si déjà running local -> ignore
        if (local?.status === "running") {
            setSelectedCommentForDetail(comment);
            setDetailPanelVisible(true);
            return;
        }

        // 2) set running local + open panel
        setMoreStateById((prev) => ({
            ...prev,
            [comment.id]: {
                status: "running",
                content: prev?.[comment.id]?.content || "",
                reasoning: prev?.[comment.id]?.reasoning || "",
                updatedAt: new Date().toISOString(),
            },
        }));

        setSelectedCommentForDetail(comment);
        setDetailPanelVisible(true);

        const moreTask = buildCommentAiTask(
            'comment-more',
            comment.id,
            analysisId || storeDocumentVersionId,
            tReview("sidebar.aiTasks.commentMemo", {
                defaultValue: "Génération du mémo commentaire",
            })
        );
        dispatch(startAiTask(moreTask));

        try {
            const documentVersionId =
                comment?.document_version_id ||
                comment?.documentVersionId ||
                storeDocumentVersionId ||
                null;

            if (!documentVersionId) {
                setMoreStateById((prev) => ({
                    ...prev,
                    [comment.id]: {
                        status: "error",
                        content: prev?.[comment.id]?.content || "",
                        reasoning: prev?.[comment.id]?.reasoning || "",
                        updatedAt: new Date().toISOString(),
                    },
                }));
                return;
            }

            // IMPORTANT: streamMoreForComment appelle un callback qui met à jour content
            const finalContent = await streamMoreForComment({
                documentVersionId,
                aiCommentId: comment.id,
                commentObj: comment,
                onChunk: (nextContent) => {
                    setMoreStateById((prev) => ({
                        ...prev,
                        [comment.id]: {
                            status: "running",
                            content: nextContent?.content || "",
                            reasoning: nextContent?.reasoning || prev?.[comment.id]?.reasoning || "",
                            updatedAt: new Date().toISOString(),
                        },
                    }));
                },
            });

            // ✅ 3) FIN DU STREAM => done
            setMoreStateById((prev) => ({
                ...prev,
                [comment.id]: {
                    status: "done",
                    content: finalContent ?? prev?.[comment.id]?.content ?? "",
                    reasoning: prev?.[comment.id]?.reasoning || "",
                    updatedAt: new Date().toISOString(),
                },
            }));
        } catch (e) {
            // ⚠️ plus besoin de 409 normalement, mais si jamais:
            if (e?.code === 409) {
                // au lieu de rester indéfiniment en running, au minimum: stop spinner
                setMoreStateById((prev) => ({
                    ...prev,
                    [comment.id]: {
                        status: "running",
                        content: prev?.[comment.id]?.content || "",
                        reasoning: prev?.[comment.id]?.reasoning || "",
                        updatedAt: new Date().toISOString(),
                    },
                }));
                return;
            }

            setMoreStateById((prev) => ({
                ...prev,
                [comment.id]: {
                    status: "error",
                    content: prev?.[comment.id]?.content || "",
                    reasoning: prev?.[comment.id]?.reasoning || "",
                    updatedAt: new Date().toISOString(),
                },
            }));
        } finally {
            dispatch(finishAiTask(moreTask.id));
        }
    };

    // Handler pour fermer le panel
    const handleDetailPanelClose = () => {
        setDetailPanelVisible(false);
        setSelectedCommentForDetail(null);
    };

    // ── Handler bouton "Lire" : enrichissement progressif via /enrichment/stream ──
    const handleReadClick = async (comment) => {
        const enrichment = comment.enrichment || {};

        // Cache hit (already done) → le composant card gère le toggle lui-même
        if (enrichment?.status === 'done') return;

        if (!fullCommentDetailsAllowed) {
            openLockedFeature("comment_details", {
                metadata_json: {
                    trial_free_comment_details_limit: trialFreeCommentDetailsLimit,
                    reason: "comment_enrichment_locked_or_unknown",
                },
            });
            return;
        }

        // Déjà en cours → ignore (pas de double appel)
        if (enrichment?.status === 'loading') return;

        // ── AbortController : timeout client + cleanup unmount ────────────────
        const abortCtrl = new AbortController();
        enrichmentAbortControllersRef.current[comment.id] = abortCtrl;

        // Flag local pour distinguer timeout client vs annulation unmount
        let didTimeout = false;
        const enrichmentTask = buildCommentAiTask(
            'comment-enrichment',
            comment.id,
            analysisId || storeDocumentVersionId,
            tReview("sidebar.aiTasks.commentEnrichment", {
                defaultValue: "Enrichissement du commentaire",
            })
        );

        // Auto-abort après ENRICHMENT_TIMEOUT_MS pour éviter loading bloqué
        const timeoutId = setTimeout(() => {
            didTimeout = true;
            abortCtrl.abort(); // sans raison custom (portabilité Safari/Firefox)
        }, ENRICHMENT_TIMEOUT_MS);

        // ✅ Mark loading dans Redux (source de vérité unique)
        dispatch({
            type: IAStreamActionTypes.PATCH_COMMENT_ENRICHMENT,
            payload: {
                id: comment.id,
                enrichment: { status: 'loading', detailed_view: null, error: null, enriched_at: null },
            },
        });
        dispatch(startAiTask(enrichmentTask));

        try {
            const res = await stream_comment_enrichment(comment.id, { signal: abortCtrl.signal });

            if (!res.ok || !res.body) {
                throw new Error(`enrichment_failed_${res.status}`);
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';
            let detailedView = null;
            let progressiveDetailedView = {};
            let finalReceived = false;

            const parseSseBlock = (block) => {
                if (!block || !block.trim()) return;

                const dataLines = block
                    .split(/\r?\n/)
                    .map((line) => line.trim())
                    .filter((line) => line.startsWith('data:'))
                    .map((line) => line.slice(5).trim())
                    .filter(Boolean);

                if (dataLines.length === 0) return;

                const rawLine = dataLines.join('\n').trim();
                if (!rawLine) return;

                let parsed = null;
                try {
                    parsed = JSON.parse(rawLine);
                } catch (parseErr) {
                    console.warn('[handleReadClick] malformed enrichment SSE chunk ignored:', parseErr);
                    return;
                }

                if (!parsed) {
                    dispatch({
                        type: IAStreamActionTypes.PATCH_COMMENT_ENRICHMENT,
                        payload: {
                            id: comment.id,
                            enrichment: {
                                status: 'error',
                                detailed_view: comment.enrichment?.detailed_view ?? null,
                                error: 'enrichment_parse_error',
                                enriched_at: null,
                            },
                        },
                    });
                    return;
                }

                if (parsed.event === 'enrichment_chunk') {
                    const field = parsed.field;
                    const value = typeof parsed.value === 'string' ? parsed.value.trim() : '';
                    if (!field || !value) return;

                    if (field === 'recommendation') {
                        const recommendationPatch = getRecommendationSections(value).reduce((acc, section) => {
                            acc[section.key] = section.text;
                            return acc;
                        }, {});
                        progressiveDetailedView = {
                            ...progressiveDetailedView,
                            recommendation: {
                                ...(progressiveDetailedView.recommendation || {}),
                                ...(Object.keys(recommendationPatch).length > 0 ? recommendationPatch : { primary: value }),
                            },
                        };
                    } else if (field.startsWith('recommendation.')) {
                        const recommendationKey = field.split('.')[1];
                        if (!['primary', 'drafting_target', 'alternative'].includes(recommendationKey)) return;
                        progressiveDetailedView = {
                            ...progressiveDetailedView,
                            recommendation: {
                                ...(progressiveDetailedView.recommendation || {}),
                                [recommendationKey]: value,
                            },
                        };
                    } else if (['problem', 'impact', 'justification'].includes(field)) {
                        progressiveDetailedView = {
                            ...progressiveDetailedView,
                            [field]: value,
                        };
                    } else {
                        return;
                    }

                    dispatch({
                        type: IAStreamActionTypes.PATCH_COMMENT_ENRICHMENT,
                        payload: {
                            id: comment.id,
                            enrichment: {
                                status: 'loading',
                                detailed_view: progressiveDetailedView,
                                error: null,
                                enriched_at: null,
                            },
                        },
                    });
                    return;
                }

                if (parsed.event && parsed.event !== 'card_enrichment') {
                    // Cas enrichment_done / done / finish : le backend signale la fin du stream
                    // sans renvoyer un detailed_view complet → on utilise progressiveDetailedView
                    const isDoneSignal = ['enrichment_done', 'done', 'finish', 'stream_done'].includes(parsed.event);
                    if (isDoneSignal && Object.keys(progressiveDetailedView).length > 0) {
                        detailedView = progressiveDetailedView;
                        finalReceived = true;
                    }
                    return;
                }

                if (parsed._parse_error || !parsed.detailed_view) {
                    // Si des chunks progressifs ont déjà été reçus, les utiliser plutôt que
                    // dispatcher une erreur (le backend n'a pas renvoyé de detailed_view final)
                    if (Object.keys(progressiveDetailedView).length > 0) {
                        console.info(
                            '[handleReadClick] no detailed_view in final event, fallback to progressive chunks',
                            { hasProgressiveFields: Object.keys(progressiveDetailedView) }
                        );
                        detailedView = progressiveDetailedView;
                        finalReceived = true;
                        return;
                    }

                    console.warn(
                        '[handleReadClick] SSE parse error or empty enrichment payload:',
                        {
                            _parse_error: parsed._parse_error,
                            hasDetailedView: !!parsed.detailed_view,
                            id: parsed.id,
                        }
                    );
                    dispatch({
                        type: IAStreamActionTypes.PATCH_COMMENT_ENRICHMENT,
                        payload: {
                            id: comment.id,
                            enrichment: {
                                status: 'error',
                                detailed_view: comment.enrichment?.detailed_view ?? null,
                                error: 'enrichment_parse_error',
                                enriched_at: null,
                            },
                        },
                    });
                    return;
                }

                if (parsed.id && String(parsed.id) !== String(comment.id)) {
                    console.warn(
                        '[handleReadClick] SSE id mismatch, accepting payload for single-comment stream:',
                        { expected: comment.id, received: parsed.id }
                    );
                }

                detailedView = parsed.detailed_view;
                finalReceived = true;
            };

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                if (!chunk) continue;
                buffer += chunk;

                let sepMatch = buffer.match(/\r?\n\r?\n/);
                while (sepMatch) {
                    const sepIdx = sepMatch.index ?? -1;
                    if (sepIdx < 0) break;
                    const block = buffer.slice(0, sepIdx);
                    buffer = buffer.slice(sepIdx + sepMatch[0].length);
                    parseSseBlock(block);
                    if (finalReceived) break;
                    sepMatch = buffer.match(/\r?\n\r?\n/);
                }
                if (finalReceived) break;
            }

            if (!finalReceived && buffer.trim()) {
                parseSseBlock(buffer);
            }

            // Fallback ultime : si le backend n'a jamais envoyé de card_enrichment avec
            // detailed_view mais a bien streamé des enrichment_chunk, on utilise les chunks
            if (!detailedView && Object.keys(progressiveDetailedView).length > 0) {
                console.info(
                    '[handleReadClick] stream ended without card_enrichment final event, ' +
                    'using progressiveDetailedView as final result',
                    { fields: Object.keys(progressiveDetailedView) }
                );
                detailedView = progressiveDetailedView;
            }

            if (!detailedView) {
                throw new Error('no_enrichment_received');
            }

            // ✅ Merge dans Redux → source de vérité unique
            dispatch({
                type: IAStreamActionTypes.PATCH_COMMENT_ENRICHMENT,
                payload: {
                    id: comment.id,
                    enrichment: {
                        status: 'done',
                        detailed_view: detailedView,
                        error: null,
                        enriched_at: new Date().toISOString(),
                    },
                },
            });
        } catch (e) {
            // ✅ Détection robuste d'annulation (timeout OU unmount)
            // - e.name === 'AbortError' : standard DOM (tous navigateurs modernes)
            // - didTimeout : notre flag local si timeout déclenché
            const isAbortError = e.name === 'AbortError';
            const isUnmount = isAbortError && !didTimeout;

            // Log uniquement les erreurs réelles (pas timeout/unmount)
            if (!isAbortError) {
                console.error('[handleReadClick] enrichment error', comment.id, e);
            }

            // Si unmount propre (annulation sans timeout) → pas d'état error
            if (isUnmount) {
                return;
            }

            // ✅ Dispatch error dans Redux
            dispatch({
                type: IAStreamActionTypes.PATCH_COMMENT_ENRICHMENT,
                payload: {
                    id: comment.id,
                    enrichment: {
                        status: 'error',
                        detailed_view: comment.enrichment?.detailed_view ?? null,
                        error: didTimeout ? 'timeout' : e.message,
                        enriched_at: null,
                    },
                },
            });
        } finally {
            clearTimeout(timeoutId);
            // ✅ Cleanup controller — évite memory leak
            delete enrichmentAbortControllersRef.current[comment.id];
            dispatch(finishAiTask(enrichmentTask.id));
        }
    };

    const {
        primary: filteredComments,
        comparisonChanges: secondaryComparisonChanges,
        uncertainComparisonChanges,
        scoped: scopedDisplayedComments,
    } = useMemo(
        () => buildSidebarCommentGroups({
            comments: displayed,
            selectedCriterion,
            sortMode,
            language: getCurrentLanguage(),
        }),
        [displayed, selectedCriterion, sortMode]
    );
    const filteredReferenceReviewCards = useMemo(
        () => filterReferenceReviewCards(referenceReviewCards, selectedCriterion),
        [referenceReviewCards, selectedCriterion]
    );

    const commentsForViewerHighlights = useMemo(
        () => [
            ...filteredComments,
            ...secondaryComparisonChanges,
            ...(SHOW_UNCERTAIN_COMPARISON_CHANGES ? uncertainComparisonChanges : []),
            ...filteredReferenceReviewCards.flatMap((card) => (
                (Array.isArray(card?.contract_evidence) ? card.contract_evidence : [])
                    .map((evidence) => ({
                        id: card.id,
                        ref: getEvidenceTargetRef(evidence),
                        source_type: card.source_type,
                    }))
                    .filter((target) => target.ref)
            )),
        ],
        [filteredComments, filteredReferenceReviewCards, secondaryComparisonChanges, uncertainComparisonChanges]
    );
    const legalSignalChipState = useMemo(
        () => buildLegalSignalChipState(filteredComments),
        [filteredComments]
    );
    const legalSignalCount = legalSignalChipState.count;
    const firstLegalSignalComment = legalSignalChipState.firstComment;

    const jumpToFirstLegalSignal = useCallback(() => {
        if (!firstLegalSignalComment) return;
        const target = getLegalSignalCommentTarget(firstLegalSignalComment);
        onCommentClick?.(target.ref, target.id);

        window.requestAnimationFrame(() => {
            const selector = target.id
                ? `[data-comment-id="${target.id}"]`
                : target.ref
                    ? `[data-ref="${target.ref}"]`
                    : null;
            const el = selector ? sidebarRef.current?.querySelector(selector) : null;
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }, [firstLegalSignalComment, onCommentClick]);

    useEffect(() => {
        onVisibleCommentsChange?.(commentsForViewerHighlights);
    }, [commentsForViewerHighlights, onVisibleCommentsChange]);

    const summaryCommentsPayload = useMemo(
        () => buildSummaryCommentsPayload(filteredComments),
        [filteredComments]
    );

    const summaryCacheKey = useMemo(
        () => buildSummaryCacheKey({
            commentsPayload: summaryCommentsPayload,
            documentVersionId: storeDocumentVersionId,
            language: getCurrentLanguage(),
        }),
        [summaryCommentsPayload, storeDocumentVersionId]
    );

    const summaryRequestPayload = useMemo(() => ({
        analyse_id: analysisId,
        document_version_id: documentVersionId || storeDocumentVersionId,
        comment_ids: filteredComments.map((comment) => comment.id).filter(Boolean),
        criterion_title: selectedCriterion || "",
        language: getCurrentLanguage(),
    }), [analysisId, documentVersionId, filteredComments, selectedCriterion, storeDocumentVersionId]);

    const currentSummaryCacheEntry = summaryCache[summaryCacheKey];
    const hasCurrentSummaryCache = isStrategicMemoCacheAvailable({
        filteredCommentsCount: filteredComments.length,
        cacheEntry: currentSummaryCacheEntry,
        strategicMemoAllowed,
    });
    const isSummaryViewActive = summaryVisible && visibleSummaryCacheKey === summaryCacheKey;

    useEffect(() => {
        const previousKey = previousSummaryCacheKeyRef.current;
        previousSummaryCacheKeyRef.current = summaryCacheKey;
        if (!previousKey || previousKey === summaryCacheKey) return;

        setSummaryVisible(false);
        setVisibleSummaryCacheKey(null);
        riskListScrollTopRef.current = 0;
        window.requestAnimationFrame(() => {
            sidebarRef.current?.scrollTo?.({ top: 0, behavior: 'auto' });
        });
    }, [summaryCacheKey]);

    const includeUncertainComparisonChanges = SHOW_UNCERTAIN_COMPARISON_CHANGES;
    const exportableCommentsCount = filteredComments.length
        + secondaryComparisonChanges.length
        + (includeUncertainComparisonChanges ? uncertainComparisonChanges.length : 0);

    const exportFilteredComments = async () => {
        if (exportableCommentsCount === 0 || isExporting) return;
        if (!standardExportsAllowed) {
            openLockedFeature("standard_export");
            return;
        }

        setIsExporting(true);
        try {
            const labels = buildCommentWorkbookLabels(tReview);
            const data = buildCommentWorkbookData({
                primaryComments: filteredComments,
                comparisonChanges: secondaryComparisonChanges,
                uncertainComparisonChanges,
                includeUncertainComparisonChanges,
                moreStateById,
                fastGlobalAnalysisById,
                labels,
                t: tReview,
            });
            await downloadCommentWorkbook({
                data,
                labels,
                filename: `verdictia-revue-${formatCommentWorkbookDate()}.xlsx`,
            });
        } catch (error) {
            console.error('comment_workbook_export_failed', error);
            message.error(tReview('sidebar.exportWorkbook.error', {
                defaultValue: "Impossible de générer le fichier d'export.",
            }));
        } finally {
            setIsExporting(false);
        }
    };

    useEffect(() => {
        if (!hasCurrentSummaryCache || isSummaryViewActive) return;
        setAiSummary(currentSummaryCacheEntry.html);
        setSummaryError(currentSummaryCacheEntry.error || null);
        setIsGeneratingSummary(false);
    }, [currentSummaryCacheEntry, hasCurrentSummaryCache, isSummaryViewActive]);

    useEffect(() => {
        if (strategicMemoAllowed) return;
        setSummaryVisible(false);
        setVisibleSummaryCacheKey(null);
        setIsGeneratingSummary(false);
    }, [strategicMemoAllowed]);

    const openSummaryView = useCallback(() => {
        const sidebar = sidebarRef.current;
        if (sidebar) {
            riskListScrollTopRef.current = sidebar.scrollTop;
        }
        setSummaryVisible(true);
        setVisibleSummaryCacheKey(summaryCacheKey);
        window.requestAnimationFrame(() => {
            sidebarRef.current?.scrollTo?.({ top: 0, behavior: 'auto' });
            summaryBackButtonRef.current?.focus?.({ preventScroll: true });
        });
    }, [summaryCacheKey]);

    const closeSummaryView = useCallback(() => {
        setSummaryVisible(false);
        window.requestAnimationFrame(() => {
            sidebarRef.current?.scrollTo?.({
                top: riskListScrollTopRef.current,
                behavior: 'auto',
            });
        });
    }, []);

    const focusStrategicMemoBlock = useCallback(() => {
        if (!focusStrategicMemoOnShow || typeof window === "undefined") return;
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                scrollStrategicMemoIntoView(sidebarRef.current);
            });
        });
    }, [focusStrategicMemoOnShow]);

    useEffect(() => {
        if (!focusStrategicMemoOnShow || !isSummaryViewActive) return;
        focusStrategicMemoBlock();
    }, [
        focusStrategicMemoBlock,
        focusStrategicMemoOnShow,
        isSummaryViewActive,
    ]);

    // Fonction pour générer le résumé IA
    const generateAISummary = async () => {
        if (filteredComments.length === 0) return;
        if (!strategicMemoAllowed) {
            openLockedFeature("strategic_memo");
            return;
        }
        onStrategicMemoOpen?.();
        openSummaryView();
        if (!summaryRequestPayload.analyse_id || !summaryRequestPayload.document_version_id || summaryRequestPayload.comment_ids.length === 0) {
            setSummaryError(tReview("sidebar.resumePanelError", {
                defaultValue: "Unable to generate the summary right now."
            }));
            setSummaryVisible(true);
            setVisibleSummaryCacheKey(summaryCacheKey);
            return;
        }

        const cached = summaryCache[summaryCacheKey];
        if (isUsableSummaryCacheEntry(cached) && cached.status === 'done') {
            setAiSummary(cached.html);
            setSummaryError(null);
            setSummaryCopied(false);
            setSummaryVisible(true);
            setVisibleSummaryCacheKey(summaryCacheKey);
            setIsGeneratingSummary(false);
            return;
        }

        const startedAt = new Date().toISOString();
        let latestSummary = '';
        setIsGeneratingSummary(true);
        setSummaryError(null);
        setAiSummary('');
        setSummaryCopied(false);
        setSummaryVisible(true);
        setVisibleSummaryCacheKey(summaryCacheKey);

        dispatch({
            type: IAStreamActionTypes.UPSERT_SUMMARY_CACHE_ENTRY,
            payload: {
                key: summaryCacheKey,
                entry: {
                    status: 'loading',
                    html: '',
                    error: null,
                    createdAt: startedAt,
                    updatedAt: startedAt,
                    commentCount: summaryCommentsPayload.length,
                    documentVersionId: storeDocumentVersionId || null,
                },
            },
        });

        const summaryTask = buildSummaryAiTask(
            summaryCacheKey,
            analysisId || storeDocumentVersionId,
            tReview("sidebar.aiTasks.summaryMemo", {
                defaultValue: "Génération du mémo IA",
            })
        );
        dispatch(startAiTask(summaryTask));

        try {
            await dispatch(
                RequestGenerateSummary(
                    summaryRequestPayload,
                    (partialSummary) => {
                        latestSummary = normalizeSummaryHtmlPayload(partialSummary);
                        setAiSummary(latestSummary);
                        dispatch({
                            type: IAStreamActionTypes.UPSERT_SUMMARY_CACHE_ENTRY,
                            payload: {
                                key: summaryCacheKey,
                                entry: {
                                    status: 'loading',
                                    html: latestSummary,
                                    error: null,
                                    updatedAt: new Date().toISOString(),
                                },
                            },
                        });

                        setSummaryVisible((prev) => {
                            if (!prev && partialSummary?.trim()) {
                                return true;
                            }
                            return prev;
                        });
                    },
                    (finalSummary) => {
                        const html = normalizeSummaryHtmlPayload(finalSummary) || latestSummary;
                        setIsGeneratingSummary(false);
                        setAiSummary(html);
                        setSummaryVisible(true);
                        setVisibleSummaryCacheKey(summaryCacheKey);
                        dispatch({
                            type: IAStreamActionTypes.UPSERT_SUMMARY_CACHE_ENTRY,
                            payload: {
                                key: summaryCacheKey,
                                entry: {
                                    status: 'done',
                                    html,
                                    error: null,
                                    updatedAt: new Date().toISOString(),
                                },
                            },
                        });
                    },
                    (err) => {
                        const errorMessage = tReview("sidebar.resumePanelError", {
                            defaultValue: "Unable to generate the summary right now."
                        });
                        setIsGeneratingSummary(false);
                        setSummaryVisible(true);
                        setVisibleSummaryCacheKey(summaryCacheKey);
                        setSummaryError(errorMessage);
                        dispatch({
                            type: IAStreamActionTypes.UPSERT_SUMMARY_CACHE_ENTRY,
                            payload: {
                                key: summaryCacheKey,
                                entry: {
                                    status: 'error',
                                    html: latestSummary,
                                    error: err?.message || errorMessage,
                                    updatedAt: new Date().toISOString(),
                                },
                            },
                        });
                    }
                )
            );
        } finally {
            dispatch(finishAiTask(summaryTask.id));
        }
    };

    // Calculer le nombre total de commentaires et le nombre de commentaires filtrés
    const totalCommentsCount = (displayed || []).filter((comment) => isPrimaryRiskComment(comment)).length
        + referenceReviewCards.length;
    const filteredCommentsCount = filteredComments.length;
    const filteredViewCardsCount = filteredCommentsCount + filteredReferenceReviewCards.length;
    const hasRenderableCards = filteredViewCardsCount > 0;
    const directSourceStatus = useMemo(() => (
        povResultsVisible && (!analysisStreamMode || analysisStreamMode === 'fast_global')
            ? getDirectSourceStatus({ comments, terminal: fastGlobalTerminal, analysisId, isStreaming: ia_streaming })
            : null
    ), [comments, fastGlobalTerminal, analysisId, ia_streaming, povResultsVisible, analysisStreamMode]);
    const globalResultStatus = analysisReasoningStatus === 'idle'
        ? directSourceStatus?.status || analysisReasoningStatus
        : analysisReasoningStatus;
    const sourceLimitations = ['failed', 'cancelled'].includes(globalResultStatus)
        ? [] : directSourceStatus?.limitations || [];
    const hasSourceLimitations = sourceLimitations.length > 0;
    const compactGlobalProgress = (analysisStreamMode === 'fast_global' || Boolean(directSourceStatus))
        && (hasRenderableCards || globalResultStatus === 'partial'
            || (hasProviderSummary && !ia_streaming && ['failed', 'partial'].includes(analysisReasoningStatus)) || (
            fastGlobalObservations.length === 0
            && fastGlobalThinkingItems.length === 0
            && analysisReasoningStatus !== 'partial'
        ));
    const structuredProgressSidebarActive = analysisStreamMode === 'comparison'
        && !compactGlobalProgress
        && !fastGlobalCardsStarted
        && !hasRenderableCards;
    const providerSummaryReading = analysisStreamMode === 'fast_global' && providerSummary?.status === 'active' && ia_streaming
        && !fastGlobalCardsStarted && !isSummaryViewActive;
    const referenceReviewScopeVisible = !selectedCriterion
        || referenceReviewCriterionName === selectedCriterion;
    const referenceReviewProgressActive = referenceReviewScopeVisible
        && analysisStreamMode === 'reference_review'
        && ia_streaming;
    const referenceReviewHydrationUnavailable = referenceReviewScopeVisible
        && ['error', 'incompatible'].includes(referenceReview?.hydration_status);
    const referenceReviewHydrationAbsent = referenceReviewScopeVisible
        && referenceReview?.hydration_status === 'absent';
    const referenceReviewFailed = referenceReviewScopeVisible
        && referenceReview?.status === 'failed';
    const referenceReviewRequirementCount = (referenceReview?.requirement_order || []).length;
    const currentReferenceReviewReviewedCount = Object.keys(referenceReview?.verdicts_by_id || {}).length;
    const referenceReviewReviewedCount = currentReferenceReviewReviewedCount > 0
        ? currentReferenceReviewReviewedCount
        : filteredReferenceReviewCards.length;
    const referenceReviewSectionVisible = referenceReviewScopeVisible && (
        filteredReferenceReviewCards.length > 0
        || referenceReviewRequirementCount > 0
        || referenceReviewProgressActive
        || referenceReviewHydrationUnavailable
        || referenceReviewFailed
    );
    const directGlobalComments = useMemo(
        () => filteredComments.filter(isDirectGlobalCard),
        [filteredComments]
    );
    const ordinaryComments = useMemo(
        () => filteredComments.filter((comment) => !isDirectGlobalCard(comment)),
        [filteredComments]
    );
    const priorityComments = useMemo(
        () => ordinaryComments.filter((comment) => (
            isPriorityRiskComment(comment) && !isFastGlobalToConfirmComment(comment)
        )),
        [ordinaryComments]
    );
    const standardComments = useMemo(
        () => ordinaryComments.filter((comment) => (
            !isPriorityRiskComment(comment) && !isFastGlobalToConfirmComment(comment)
        )),
        [ordinaryComments]
    );
    const toConfirmComments = useMemo(
        () => ordinaryComments.filter(isFastGlobalToConfirmComment),
        [ordinaryComments]
    );
    const globalPriorityState = getGlobalPriorityState(filteredComments);
    const globalPriorityNotice = globalPriorityState === 'final'
        ? sortMode === DEFAULT_COMMENT_SORT ? 'final' : 'custom'
        : ia_streaming ? 'provisional' : 'unavailable';
    const hasOrdinaryCommentResults = filteredComments.length > 0
        || secondaryComparisonChanges.length > 0
        || (SHOW_UNCERTAIN_COMPARISON_CHANGES && uncertainComparisonChanges.length > 0);
    const materialCoverageDiagnostic = useMemo(
        () => directSourceStatus ? null : getMaterialCoverageDiagnostic(scopedDisplayedComments),
        [scopedDisplayedComments, directSourceStatus]
    );
    const shouldShowMaterialCoverageWarning = shouldShowMaterialCoverageDiagnostic()
        && Boolean(materialCoverageDiagnostic)
        && !hideMaterialCoverageWarning;

    useEffect(() => {
        setHideMaterialCoverageWarning(false);
    }, [analysisId]);

    useEffect(() => {
        if (!onAutomatedResultsBannerVisibilityChange) return undefined;

        const root = sidebarRef.current;
        const banner = automatedResultsBannerRef.current;
        if (!root || !banner || filteredViewCardsCount === 0 || detailPanelVisible) {
            onAutomatedResultsBannerVisibilityChange(false);
            return undefined;
        }

        const clippingElement = root.parentElement;
        let animationFrame = null;
        const updateVisibility = () => {
            if (animationFrame !== null) return;
            animationFrame = window.requestAnimationFrame(() => {
                animationFrame = null;
                const stickyHeader = root.querySelector('.comments-sticky-header');
                const bannerVisible = isAutomatedResultsBannerVisible({
                    rootRect: root.getBoundingClientRect(),
                    bannerRect: banner.getBoundingClientRect(),
                    stickyHeaderRect: stickyHeader?.getBoundingClientRect() || null,
                    clippingRect: clippingElement?.getBoundingClientRect() || null,
                });
                onAutomatedResultsBannerVisibilityChange(!bannerVisible);
            });
        };

        const scrollElements = [root, clippingElement].filter(Boolean);
        scrollElements.forEach((element) => element.addEventListener('scroll', updateVisibility, { passive: true }));
        window.addEventListener('resize', updateVisibility);
        updateVisibility();

        return () => {
            if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
            scrollElements.forEach((element) => element.removeEventListener('scroll', updateVisibility));
            window.removeEventListener('resize', updateVisibility);
            onAutomatedResultsBannerVisibilityChange(false);
        };
    }, [detailPanelVisible, filteredViewCardsCount, onAutomatedResultsBannerVisibilityChange]);
    const secondaryComparisonChangesCount = secondaryComparisonChanges.length;
    const uncertainComparisonChangesCount = uncertainComparisonChanges.length;
    const hasSecondaryComparisonResults = secondaryComparisonChangesCount > 0
        || (SHOW_UNCERTAIN_COMPARISON_CHANGES && uncertainComparisonChangesCount > 0);
    const hasAnyComments = (Array.isArray(comments) && comments.length > 0)
        || referenceReviewCards.length > 0;
    const fastGlobalCompletedEmpty = analysisStreamMode === 'fast_global'
        && fastGlobalTerminal?.completion_status === 'completed'
        && fastGlobalTerminal?.outcome_code === 'completed_no_findings';
    const hasFailedFastGlobalCriterion = Object.values(fastGlobalCriteriaStatus)
        .some((item) => item?.status === 'failed');
    const emptyCommentsDescription = !hasCompleteAnalysisPov
        ? tReview("pov_scope.select")
        : referenceReviewHydrationAbsent
            ? tReview("referenceReview.emptyForPov")
            : selectedCriterion && hasAnyComments
                ? tReview("sidebar.emptyFiltered")
                : fastGlobalCompletedEmpty
                    ? tReview("sidebar.emptyNoFindings")
                : ia_streaming
                    ? tReview("sidebar.emptyStreaming")
                    : tReview("pov_scope.noResults");
    useEffect(() => {
        const storage = typeof window !== "undefined" ? window.localStorage : null;
        const seen = hasSeenPostAnalysisSuccessCard(storage, postAnalysisSuccessStorageKey);
        if (!shouldShowPostAnalysisSuccessCard({
            commentsCount: comments?.length || 0,
            iaStreaming: ia_streaming,
            seen,
            storageKey: postAnalysisSuccessStorageKey,
        })) {
            return;
        }

        setPostAnalysisSuccessVisible(true);
        markPostAnalysisSuccessCardSeen(storage, postAnalysisSuccessStorageKey);
    }, [comments?.length, ia_streaming, postAnalysisSuccessStorageKey]);
    const activeSortLabel = sortMode !== DEFAULT_COMMENT_SORT
        ? tReview(`sidebar.sort.${sortMode}`)
        : null;
    const reasoningFeedEnabled = process.env.REACT_APP_ANALYSIS_REASONING_FEED_ENABLED !== "false";
    const providerHint = String(
        process.env.REACT_APP_REVIEW_MAIN_PROVIDER
        || process.env.REACT_APP_LLM_PROVIDER
        || "bedrock"
    ).toLowerCase();
    const isAnthropicLikeProvider =
        providerHint.includes("bedrock")
        || providerHint.includes("anthropic")
        || providerHint.includes("claude");
    const fallbackReasoningMessage = reasoningFallbackRotation[reasoningFallbackIndex];

    // Pre-process: insert a line break after each sentence boundary so the user
    // reads one sentence at a time instead of a wall of text.
    // Heuristic: [.!?] + space + Uppercase-then-lowercase = real sentence start
    // (filters out abbreviations like "U.S.A." or "Cf." where next char is uppercase-only)
    const sentencedReasoningText = analysisReasoningText
        ? analysisReasoningText
            .replace(/([a-zàâçéèêëîïôûùüÿæœ])([A-ZÀÂÇÉÈÊËÎÏÔÛÙÜŸÆŒ])/g, '$1 $2') // mots collés ex: "etListes"
            .replace(/([.!?,;:])([^\s\n])/g, '$1 $2')                               // ponctuation collée
            .replace(/(\w)(de\$|à\$|de€|à€)/g, '$1 $2')                            // "de$10K" → "de $10K"
            .replace(/Saa\s+S\b/g, 'SaaS')                                          // "Saa S" → "SaaS"
            .replace(/([.!?]) +(?=[A-ZÀ-ÜÉÈÊËÂÔÙÛÇ][a-zà-üéèêëâôùûç])/g, '$1\n') // coupure de phrase
            .replace(/\n{3,}/g, '\n\n')                                             // normalise sauts
        : analysisReasoningText;

    // Slow typewriter: buffer all incoming text, reveal at a calm reading pace.
    // Data arrives fast from the backend and is cached in the hook's ref —
    // the user sees a continuous slow drip even if the LLM finishes early.
    // catchupAtChars=Infinity → never snap-forward, always drip char-by-char.
    // Typewriter désactivé — affichage direct du texte brut
    const renderedReasoningText = sentencedReasoningText || '';
    const typewriterDone = true;

    // Auto-scroll the reasoning feed to the latest content while streaming,
    // unless the user scrolled up manually.
    // Fires on every typewriter tick so the view always sticks to the new chars.
    // Uses instant scrollTop assignment — smooth scroll lags behind fast bursts.
    // Must be declared AFTER renderedReasoningText (const — temporal dead zone).
    useEffect(() => {
        if (!reasoningAutoScroll || reasoningCollapsed) return;
        const el = reasoningFeedScrollRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, [renderedReasoningText, reasoningAutoScroll, reasoningCollapsed]);

    const showReasoningFeed = reasoningFeedEnabled
        && !structuredProgressVisible
        && analysisStreamMode !== 'reference_review'
        && !reasoningHidden
        && (
            analysisReasoningText.length > 0
            || (ia_streaming && (analysisReasoningStatus === 'active' || isAnthropicLikeProvider))
        );
    const reasoningIsCompleted = analysisReasoningStatus === 'completed';
    const reasoningIsFailed = analysisReasoningStatus === 'failed';
    const reasoningIsPartial = analysisReasoningStatus === 'partial';
    const reasoningIsTerminal = reasoningIsCompleted || reasoningIsFailed || reasoningIsPartial;
    const reasoningToggleLabel = reasoningCollapsed
        ? tReview("sidebar.reasoningFeed.expand")
        : tReview("sidebar.reasoningFeed.collapse");
    const reasoningFeedTitle = reasoningIsFailed
        ? tReview("sidebar.reasoningFeed.failedTitle")
        : reasoningIsPartial
            ? tReview("sidebar.fastGlobalTimeline.partialTerminalTitle")
            : tReview("sidebar.reasoningFeed.title");
    const reasoningFeedSubtitle = reasoningIsFailed || reasoningIsPartial
        ? tReview("sidebar.reasoningFeed.failedSubtitle")
        : reasoningIsCompleted
            ? tReview("sidebar.reasoningFeed.completeSubtitle", {
                action: reasoningToggleLabel,
            })
            : tReview("sidebar.reasoningFeed.activeSubtitle");
    const reasoningScrollBottomLabel = tReview("sidebar.reasoningFeed.scrollBottom");
    const selectedDetailComment = selectedCommentForDetail
        ? {
            ...selectedCommentForDetail,
            more_status:
                moreStateById[selectedCommentForDetail.id]?.status ||
                selectedCommentForDetail?.more_status ||
                "idle",
            more_content:
                moreStateById[selectedCommentForDetail.id]?.content ||
                selectedCommentForDetail?.more_content ||
                null,
            more_reasoning:
                moreStateById[selectedCommentForDetail.id]?.reasoning ||
                selectedCommentForDetail?.more_reasoning ||
                "",
        }
        : null;

    const renderCommentCard = (c, { secondary = false, secondaryCardClass = '' } = {}) => {
        const cardRef = citationCommentRef(c) || getHighlightableCommentRef(c);
        const metadata = getComparisonMetadata(c) || null;
        const isFastGlobalCard = metadata?.review_mode === 'fast_global';
        const isCommercialPlaceholder = Boolean(
            metadata?.commercial_locked || metadata?.locked_placeholder
        );
        const isHoveredCard = hoveredCommentId === c.id;
        const isSelectedCard = selectedCommentId
            ? selectedCommentId === c.id
            : Boolean(selectedRef && cardRef === selectedRef);

        const local = moreStateById[c.id];

        const effectiveMoreContent =
            local?.content ?? c.more_content ?? null;

        let effectiveMoreStatus =
            local?.status ?? c.more_status ?? "idle";

        const effectiveMoreUpdatedAt =
            local?.updatedAt ?? c.moreUpdatedAt ?? null;

        if (effectiveMoreContent && effectiveMoreStatus === "running") {
            effectiveMoreStatus = "done";
        }

        if (effectiveMoreContent && (effectiveMoreStatus === "idle" || !effectiveMoreStatus)) {
            effectiveMoreStatus = "done";
        }
        const hasReplayableMore = Boolean(effectiveMoreContent);
        const hasReplayableDetails = Boolean(c.enrichment?.status === "done" || c.detailed_view);
        const readLocked = isCommercialPlaceholder || (!fullCommentDetailsAllowed && !hasReplayableDetails);
        const moreLocked = isCommercialPlaceholder || (!fullCommentDetailsAllowed && !hasReplayableMore);
        return (
            <AICommentCard
                key={getCommentRenderKey(c)}
                id={`comment-${c.id}`}
                className={`${exitingIds.has(c.id) ? 'is-exiting' : ''} ${isHoveredCard ? 'is-hovered-card' : ''} ${secondary ? `comparison-change-secondary-card ${secondaryCardClass}` : ''}`}
                criterionName={c.criterion_title}
                title={c.title}
                description={c.description || c.quick_view?.description || ''}
                date={c.created_at}
                doc_ref={c.doc_ref || null}
                quote={c.quote || null}
                dataRef={cardRef}
                dataCommentId={c.id}
                commentType={c.comment_type || c.commentType || null}
                metadataJson={metadata}
                onVersionEvidenceClick={onVersionEvidenceClick}
                isFastGlobal={isFastGlobalCard}
                targetBlockIds={c.target_block_ids || c.targetBlockIds || null}
                riskLevel={secondary ? null : c.risk}
                riskRationale={
                    secondary
                        ? null
                        : c.risk_rationale
                        || c.quick_view?.risk_rationale
                        || c.quick_view?.riskRationale
                        || null
                }
                page={c.page || null}
                quickView={c.quick_view || null}
                detailedView={c.detailed_view || null}
                priorityLabel={secondary ? null : c.priority_label || null}
                legalQualification={secondary ? null : c.detailed_view?.legal_qualification || null}
                probability={secondary ? null : c.probability || null}
                businessImpact={secondary ? null : c.impact || c.detailed_view?.impact || null}
                isHovered={isHoveredCard}
                isSelected={isSelectedCard}
                collapsible={!secondary}
                disclosureOpen={expandedCommentId === c.id || expandedCommentId === getCommentRenderKey(c)}
                onDisclosureToggle={() => {
                    setExpandedCommentId((current) => current === c.id || current === getCommentRenderKey(c) ? null : getCommentRenderKey(c));
                    if (expandedCommentId !== c.id && expandedCommentId !== getCommentRenderKey(c)) handleCardClick(c.id, cardRef, buildCommentCitationNavigation(c));
                }}
                onDisclosureOpen={() => setExpandedCommentId(getCommentRenderKey(c))}
                onMouseEnter={() => handleCardHover(c.id, cardRef)}
                onMouseLeave={handleCardLeave}
                toggleComparisonDiffOnCardClick={secondary}
                onClick={() => {
                    if (secondary) {
                        handleCardClick(c.id, cardRef, buildCommentCitationNavigation(c));
                        return;
                    }
                    handleCardClick(c.id, cardRef, buildCommentCitationNavigation(c));
                }}
                onMoreClick={secondary || isCommercialPlaceholder ? null : () => handleMoreClick(c)}
                fastGlobalDetail={fastGlobalAnalysisById[c.id] || null}
                fastGlobalOpenInPanel={isFastGlobalCard}
                fastGlobalFollowUpDisabled={isFastGlobalCard && (ia_streaming || metadata?.snapshot_persisted === false)}
                moreLocked={moreLocked}
                moreStatus={effectiveMoreStatus}
                moreContent={effectiveMoreContent}
                moreUpdatedAt={effectiveMoreUpdatedAt}
                pageLoading={Boolean(isPageLoading || isGlobalLoading || ia_comments_loading)}
                enrichment={c.enrichment || {}}
                onReadClick={secondary || isCommercialPlaceholder ? null : () => handleReadClick(c)}
                readLocked={readLocked}
                lockedTooltip={lockedFeatureTooltip}
                jurisprudence={
                    secondary
                        ? null
                        : c.legal_signals?.jurisprudence_trend
                        || c.jurisprudence
                        || null
                }
                legalBase={
                    secondary
                        ? null
                        : c.legal_signals?.legal_base_suggestion
                        || c.legal_base
                        || null
                }
                enforceabilityScore={secondary ? null : c.legal_signals?.enforceability_score ?? null}
                labelsVariant={labelsVariant}
            />
        );
    };

    const renderReferenceReviewCard = (card) => (
        <ReferenceReviewCard
            key={card.id}
            card={card}
            isSelected={selectedCommentId === card.id}
            onContractEvidenceClick={(sourceRef) => handleCardClick(card.id, sourceRef)}
            onReferenceEvidenceClick={(evidence, navigation) => onReferenceEvidenceClick?.(card, evidence, navigation)}
        />
    );

    const renderReferenceReviewSection = () => {
        if (!referenceReviewSectionVisible) return null;
        const total = Math.max(referenceReviewRequirementCount, filteredReferenceReviewCards.length);
        return (
            <section
                className={`reference-review-results-section ${referenceReviewProgressActive ? 'is-streaming' : ''}`.trim()}
                aria-label={tReview('referenceReview.resultsSectionTitle')}
            >
                <header className="reference-review-results-section__header">
                    <div>
                        <span>{tReview('referenceReview.eyebrow')}</span>
                        <strong>{tReview('referenceReview.resultsSectionTitle')}</strong>
                    </div>
                    {total > 0 && (
                        <small>
                            {tReview('referenceReview.resultsSectionProgress', {
                                reviewed: referenceReviewReviewedCount,
                                total,
                            })}
                        </small>
                    )}
                </header>
                <ReferenceReviewProgressNotice
                    review={referenceReview}
                    streaming={analysisStreamMode === 'reference_review' && ia_streaming}
                />
                {filteredReferenceReviewCards.length > 0 && (
                    <div className="reference-review-results-section__cards">
                        {filteredReferenceReviewCards.map(renderReferenceReviewCard)}
                    </div>
                )}
            </section>
        );
    };

    const renderComparisonChangeSection = ({
        changes,
        expanded,
        onToggle,
        label,
        className = '',
        secondaryCardClass = '',
    }) => {
        if (!changes.length) return null;
        return (
            <section className={`comparison-secondary-section ${className}`.trim()}>
                <button
                    type="button"
                    className="comparison-secondary-toggle"
                    onClick={onToggle}
                    aria-expanded={expanded}
                >
                    <span>{label}</span>
                    <span className={`comparison-secondary-toggle__chevron ${expanded ? 'is-open' : ''}`}>
                        <DownOutlined />
                    </span>
                </button>
                {expanded && (
                    <div className="comparison-secondary-list">
                        {changes.map((c) => renderCommentCard(c, {
                            secondary: true,
                            secondaryCardClass,
                        }))}
                    </div>
                )}
            </section>
        );
    };

    return (
        <>
            <div
                ref={sidebarRef}
                data-sidebar="comments"
                className={[
                    structuredProgressSidebarActive ? 'fast-global-sidebar-active' : '',
                    providerSummaryReading ? 'provider-summary-reading' : '',
                ].filter(Boolean).join(' ') || undefined}
                style={{
                    height: '100%',
                    overflowY: structuredProgressSidebarActive || detailPanelVisible
                        ? 'hidden'
                        : 'auto',
                    overflowX: 'hidden',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    paddingBottom: '16px',
                    paddingTop: '0',
                    position: 'relative',
                    boxSizing: 'border-box'
                }}>

                {detailPanelVisible ? (
                    <AICommentDetailPanel
                        visible={detailPanelVisible}
                        onClose={handleDetailPanelClose}
                        comment={selectedDetailComment}
                        labelsVariant={labelsVariant}
                        fastGlobalDetail={selectedCommentForDetail?.id ? fastGlobalAnalysisById[selectedCommentForDetail.id] || null : null}
                        onFastGlobalRetry={selectedCommentForDetail ? () => handleMoreClick(selectedCommentForDetail) : null}
                    />
                ) : (
                    <>

                {analysisStreamMode === 'fast_global' && Object.keys(fastGlobalCriteriaStatus).length > 1 && (
                    <section className="fast-global-criterion-statuses" aria-label="État des critères analysés">
                        {Object.values(fastGlobalCriteriaStatus)
                            .sort((left, right) => (left.criterion_order ?? 0) - (right.criterion_order ?? 0))
                            .map((item) => {
                                const status = fastGlobalTerminal?.completion_status === 'failed'
                                    && !hasFailedFastGlobalCriterion
                                    ? 'failed'
                                    : item.status || 'pending';
                                return (
                                    <div className={`fast-global-criterion-status is-${status}`} key={item.criterion_id}>
                                        <span>{item.criterion_title || tReview('sidebar.fastGlobalTimeline.criteria.fallback', {
                                            count: Number(item.criterion_order ?? 0) + 1,
                                        })}</span>
                                        <strong>{tReview(`sidebar.fastGlobalTimeline.criteria.status.${status}`)}</strong>
                                    </div>
                                );
                            })}
                    </section>
                )}

                {/* Sticky toolbar avec filtre - VERSION COMPACTE */}
                <div
                    className="comments-sticky-header"
                    style={{
                        marginBottom: isSummaryViewActive ? 0 : 12,
                        paddingTop: isSummaryViewActive ? "4px" : "12px",
                        paddingBottom: isSummaryViewActive ? "4px" : "12px",
                        top: 0,
                        zIndex: 10,
                        backgroundColor: '#fff',
                        position: 'sticky',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px'
                    }}
                >

                    {isSummaryViewActive ? (
                        <div className="ai-summary-view__toolbar">
                            <button
                                ref={summaryBackButtonRef}
                                type="button"
                                className="ai-summary-view__back"
                                onClick={closeSummaryView}
                            >
                                <ArrowLeftOutlined aria-hidden="true" />
                                <span>{tReview("sidebar.resumePanelBack", { defaultValue: "Retour aux risques" })}</span>
                            </button>

                            <Tooltip title={summaryCopied
                                ? tReview("sidebar.resumePanelCopied", { defaultValue: "Copié" })
                                : tReview("sidebar.resumePanelCopy", { defaultValue: "Copier" })}
                            >
                                <Button
                                    type="text"
                                    className={`ai-summary-icon-btn ${summaryCopied ? 'is-copied' : ''}`}
                                    icon={summaryCopied ? <CheckOutlined /> : <CopyOutlined />}
                                    onClick={copySummaryToClipboard}
                                    disabled={!aiSummary?.trim()}
                                    aria-label={tReview("sidebar.resumePanelCopy", { defaultValue: "Copier" })}
                                />
                            </Tooltip>
                        </div>
                    ) : (
                        <>
                    {legalSignalCount > 0 && (
                        <div className="comments-status-strip">
                            <button
                                type="button"
                                className="comments-legal-signal-chip"
                                onClick={jumpToFirstLegalSignal}
                                aria-label={tReview("sidebar.legalSignalsJump", {
                                    count: legalSignalCount,
                                })}
                            >
                                <WarningOutlined aria-hidden="true" />
                                <span>
                                    {tReview("sidebar.legalSignalsDetected", {
                                        count: legalSignalCount,
                                    })}
                                </span>
                                <span className="comments-legal-signal-chip__action">
                                    {tReview("sidebar.legalSignalsFirst")}
                                </span>
                            </button>
                        </div>
                    )}

                    {/* Ligne principale: Filtre + Bouton résumé compact */}
                    <div className="comments-toolbar-row">
                            <Select
                                    placeholder={tReview("sidebar.filter")}
                                    aria-label={tReview("sidebar.filter")}
                                    value={selectedCriterion}
                                    style={{ flex: 1, minWidth: 0 }}
                                    allowClear
                                    onChange={(value) => setSelectedCriterion(value)}
                                    className={selectedCriterion ? 'comments-filter-select comments-filter-select--has-count' : 'comments-filter-select'}
                                    suffixIcon={
                                        selectedCriterion ? (
                                            <span
                                                className="comments-filter-count"
                                                title={`${filteredViewCardsCount} / ${totalCommentsCount}`}
                                                aria-label={`${filteredViewCardsCount} / ${totalCommentsCount}`}
                                            >
                                                <strong>{filteredViewCardsCount}</strong>
                                                <span className="comments-filter-count__sep">/</span>
                                                <span className="comments-filter-count__total">{totalCommentsCount}</span>
                                            </span>
                                        ) : undefined
                                    }
                                >
                            {criterionOptions?.map((criterion) => (
                                <Option key={criterion} value={criterion}>
                                    {criterion}
                                </Option>
                            ))}
                        </Select>

                        <Tooltip
                            title={tReview('sidebar.sort.ariaLabel')}
                            mouseEnterDelay={0}
                            mouseLeaveDelay={0}
                        >
                            <span className="comments-toolbar-action-wrapper">
                                <Dropdown
                                    trigger={['click']}
                                    placement="bottomRight"
                                    menu={{
                                        items: commentSortMenuItems,
                                        onClick: ({ key }) => setSortMode(key),
                                    }}
                                >
                                    <Button
                                        icon={<SortAscendingOutlined />}
                                        title={tReview('sidebar.sort.ariaLabel')}
                                        aria-label={tReview('sidebar.sort.ariaLabel')}
                                        className={`comments-sort-inline-trigger ${sortMode !== DEFAULT_COMMENT_SORT ? 'is-active' : ''}`}
                                    />
                                </Dropdown>
                            </span>
                        </Tooltip>

                        {exportableCommentsCount > 0 && (
                            <Tooltip
                                title={tReview("sidebar.exportComments")}
                                mouseEnterDelay={0}
                                mouseLeaveDelay={0}
                            >
                                <Button
                                    type="default"
                                    icon={standardExportsAllowed ? <DownloadOutlined/> : <LockOutlined />}
                                    onClick={exportFilteredComments}
                                    loading={standardExportsAllowed && isExporting}
                                    disabled={standardExportsAllowed && isExporting}
                                    title={tReview("sidebar.exportComments")}
                                    aria-label={tReview("sidebar.exportComments")}
                                    className={`ai-comments-export-trigger ${standardExportsAllowed ? "" : "is-commercial-locked"}`}
                                />
                            </Tooltip>
                        )}

                        {/* Bouton résumé IA - version compacte icon-only */}
                        {filteredCommentsCount > 0 && (
                            <>
                                <Tooltip
                                    title={hasCurrentSummaryCache
                                        ? tReview("sidebar.resumePanelView", { defaultValue: "Voir le résumé" })
                                        : tReview("sidebar.resumeModalTitle")}
                                    mouseEnterDelay={0}
                                    mouseLeaveDelay={0}
                                >
                                    <Button
                                        type="primary"
                                        icon={strategicMemoAllowed ? <RobotOutlined/> : <LockOutlined />}
                                        onClick={generateAISummary}
                                        loading={strategicMemoAllowed && isGeneratingSummary}
                                        disabled={strategicMemoAllowed && isGeneratingSummary}
                                        title={tReview("sidebar.resumeModalTitle")}
                                        aria-label={tReview("sidebar.resumeModalTitle")}
                                        className={`ai-summary-trigger ${hasCurrentSummaryCache ? "has-current-summary" : ""} ${strategicMemoAllowed ? "" : "is-commercial-locked"}`}
                                    />
                                </Tooltip>
                            </>
                        )}
                    </div>

                    {activeSortLabel && (
                        <div className="comments-sort-chip-row">
                            <span className="comments-sort-chip">
                                <span className="comments-sort-chip__label">
                                    {tReview("sidebar.sort.activeLabel")}
                                </span>
                                <span>{activeSortLabel}</span>
                            </span>
                        </div>
                    )}

                    {postAnalysisSuccessVisible && (
                        <section
                            className="post-analysis-success-card"
                            aria-label={tReview("sidebar.postAnalysisSuccess.ariaLabel")}
                        >
                            <div className="post-analysis-success-card__header">
                                <div className="post-analysis-success-card__copy">
                                    <span className="post-analysis-success-card__eyebrow">
                                        <CheckOutlined aria-hidden="true" />
                                        {tReview("sidebar.postAnalysisSuccess.eyebrow")}
                                    </span>
                                    <h3>
                                        {tReview("sidebar.postAnalysisSuccess.title")}
                                    </h3>
                                </div>
                                <button
                                    type="button"
                                    className="post-analysis-success-card__close"
                                    onClick={() => setPostAnalysisSuccessVisible(false)}
                                    aria-label={tReview("sidebar.postAnalysisSuccess.dismiss")}
                                >
                                    <CloseOutlined aria-hidden="true" />
                                </button>
                            </div>
                            <p>
                                {tReview("sidebar.postAnalysisSuccess.description")}
                            </p>
                            <Button
                                size="small"
                                className="post-analysis-success-card__cta"
                                icon={<ArrowRightOutlined />}
                                onClick={openPostAnalysisOffers}
                            >
                                {tReview("sidebar.postAnalysisSuccess.cta")}
                            </Button>
                        </section>
                    )}

                    {compactGlobalProgress && !ia_streaming
                        && (globalResultStatus === 'failed'
                            || (globalResultStatus === 'partial' && !hasSourceLimitations)) && (
                        <div className="fast-global-progressive-cards-status is-incomplete" role="status">
                            <span aria-hidden="true" />
                            {tReview(globalResultStatus === 'partial'
                                ? 'sidebar.fastGlobalPartial'
                                : hasRenderableCards
                                    ? 'sidebar.fastGlobalInterruptedWithCards'
                                    : 'sidebar.fastGlobalInterrupted')}
                        </div>
                    )}

                    {structuredProgressSidebarActive && (
                        <FastGlobalObservations
                            observations={fastGlobalObservations}
                            thinkingItems={fastGlobalThinkingItems}
                            isLoading={ia_streaming}
                            analysisStarted={fastGlobalAnalysisStarted}
                            mode={analysisStreamMode}
                            terminalStatus={analysisReasoningStatus}
                        />
                    )}

                    {showReasoningFeed && (
                        <section
                            className={`analysis-progress-feed ${reasoningCollapsed ? 'is-collapsed' : ''} ${reasoningIsCompleted ? 'is-complete' : reasoningIsFailed ? 'is-failed' : reasoningIsPartial ? 'is-partial' : 'is-active'}`}
                            aria-live="polite"
                        >
                            <div
                                className="analysis-progress-feed__header"
                                role="button"
                                tabIndex={0}
                                onClick={() => setReasoningCollapsed((prev) => !prev)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setReasoningCollapsed((prev) => !prev);
                                    }
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                <span className="analysis-progress-feed__pulse">
                                    <Loading3QuartersOutlined spin={!reasoningIsTerminal} />
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="analysis-progress-feed__title">
                                        {reasoningFeedTitle}
                                    </div>
                                    <div className="analysis-progress-feed__subtitle">
                                        {reasoningFeedSubtitle}
                                    </div>
                                </div>
                                <span
                                    className="analysis-progress-feed__chevron"
                                    style={{
                                        transform: reasoningCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                                        transition: 'transform 0.2s ease',
                                        opacity: 0.6,
                                        fontSize: 12,
                                    }}
                                >
                                    ▼
                                </span>
                            </div>
                            {!reasoningCollapsed && (
                                <div
                                    ref={reasoningFeedScrollRef}
                                    className="analysis-progress-feed__messages"
                                    onScroll={(e) => {
                                        const el = e.currentTarget;
                                        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
                                        // < 40px = user is back at bottom → re-enable auto-scroll
                                        const atBottom = distanceFromBottom < 40;
                                        setReasoningAutoScroll(atBottom);
                                    }}
                                    style={{
                                        maxHeight: reasoningIsTerminal ? 320 : 240,
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                    }}
                                >
                                    <div className="analysis-progress-feed__prose">
                                        {renderedReasoningText ? (
                                            <LineReveal
                                                text={renderedReasoningText}
                                                isStreaming={!typewriterDone || ia_streaming}
                                            />
                                        ) : (
                                            <p className="analysis-progress-feed__paragraph">
                                                {fallbackReasoningMessage}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                            {!reasoningCollapsed && !reasoningAutoScroll && analysisReasoningText.length > 0 && (
                                <button
                                    type="button"
                                    className="analysis-progress-feed__scroll-bottom"
                                    aria-label={reasoningScrollBottomLabel}
                                    title={reasoningScrollBottomLabel}
                                    onClick={() => {
                                        const el = reasoningFeedScrollRef.current;
                                        if (!el) return;
                                        el.scrollTop = el.scrollHeight;
                                        setReasoningAutoScroll(true);
                                    }}
                                >
                                    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path d="M3.5 6.5L8 11l4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                            )}
                        </section>
                    )}

                        </>
                    )}

                </div>

                {!isSummaryViewActive && hasSourceLimitations && (
                    <Alert
                        type="warning"
                        showIcon
                        message={tReview('sidebar.sourceLimitations.title')}
                        description={(
                            <>
                                {sourceLimitations.map((item, index) => (
                                    <div key={`${item.file_id || 'document'}-${index}`}>
                                        <strong>{typeof item.document_name === 'string' && item.document_name.trim()
                                            ? item.document_name
                                            : tReview(['primary', 'annex'].includes(item.document_role)
                                                ? `sidebar.sourceLimitations.${item.document_role}`
                                                : 'sidebar.sourceLimitations.document')}</strong>
                                        {' — '}{tReview(`sidebar.sourceLimitations.${item.source_completeness}`)}
                                    </div>
                                ))}
                                <div>{tReview('sidebar.sourceLimitations.action')}</div>
                            </>
                        )}
                    />
                )}

                {!isSummaryViewActive && analysisStreamMode === 'fast_global' && providerSummary && (
                    <ProviderSummaryPanel
                        key={providerSummary.stream_instance_id}
                        summary={providerSummary}
                        cardsStarted={fastGlobalCardsStarted}
                        isStreaming={ia_streaming}
                        criterionStatuses={fastGlobalCriteriaStatus}
                    />
                )}

                {isSummaryViewActive ? (
                    <section
                        className={`ai-summary-brief ai-summary-view ${isGeneratingSummary ? 'is-streaming' : ''}`}
                        aria-label={tReview("sidebar.resumeModalTitle")}
                    >
                        <div className="ai-summary-brief__header">
                            <div className="ai-summary-brief__identity">
                                <span className="ai-summary-brief__mark">
                                    <RobotOutlined />
                                </span>
                                <div className="ai-summary-brief__heading">
                                    <h3>{tReview("sidebar.resumeModalTitle")}</h3>
                                    <span className="ai-summary-brief__count">
                                        {tReview("sidebar.resumePanelCount", { count: filteredCommentsCount })}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="ai-summary-brief__body">
                            <div className="ai-summary-brief__body-inner">
                                {summaryError ? (
                                    <Alert
                                        message={tReview("sidebar.resumeModalErrorTitle")}
                                        description={summaryError}
                                        type="error"
                                        showIcon
                                    />
                                ) : (
                                    <>
                                        {isGeneratingSummary && (
                                            <div className="ai-summary-brief__meta" aria-live="polite">
                                                <span className="ai-summary-brief__live">
                                                    <Loading3QuartersOutlined spin />
                                                    {tReview("sidebar.resumePanelGenerating", {
                                                        defaultValue: "Résumé en préparation"
                                                    })}
                                                </span>
                                            </div>
                                        )}

                                        <div className="ai-summary-brief__content">
                                            {aiSummary?.trim() ? (
                                                <div
                                                    className="ai-summary-brief__prose"
                                                    dangerouslySetInnerHTML={safeHtml(aiSummary)}
                                                />
                                            ) : (
                                                <div className="ai-summary-brief__skeleton" aria-hidden="true">
                                                    <span />
                                                    <span />
                                                    <span />
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </section>
                ) : (
                    <>
                {
                    povResultsError ? (
                        <Alert
                            type="error"
                            showIcon
                            message={tReview("pov_scope.resultsError")}
                            description={tReview("pov_scope.resultsErrorDescription")}
                            action={onRetryPovResults ? (
                                <Button
                                    size="small"
                                    onClick={() => Promise.resolve(onRetryPovResults()).catch(() => {})}
                                >
                                    {tReview("pov_scope.retry")}
                                </Button>
                            ) : null}
                        />
                    ) : structuredProgressVisible
                        && !fastGlobalCardsStarted
                        && !hasRenderableCards
                        && !fastGlobalCompletedEmpty ? (
                        null
                    ) : filteredViewCardsCount > 0 ? (
                        <>
                    {shouldShowMaterialCoverageWarning && (
                                <Alert
                                    className="fast-global-coverage-warning"
                                    closable
                                    onClose={() => setHideMaterialCoverageWarning(true)}
                                    type="info"
                                    showIcon
                                    message={tReview(
                                        `sidebar.materialCoverageDiagnostic.${materialCoverageDiagnostic.variant}.title`,
                                        {count: materialCoverageDiagnostic.count}
                                    )}
                                    description={tReview(
                                        `sidebar.materialCoverageDiagnostic.${materialCoverageDiagnostic.variant}.description`,
                                        {count: materialCoverageDiagnostic.count}
                                    )}
                                />
                            )}
                            <div ref={automatedResultsBannerRef}>
                                <AutomatedResultsNotice />
                            </div>
                            {renderReferenceReviewSection()}
                            {hasOrdinaryCommentResults && (
                                <section
                                    className={`ordinary-ai-results-section ${referenceReviewSectionVisible ? 'is-secondary-scope' : ''}`.trim()}
                                    aria-label={referenceReviewSectionVisible
                                        ? tReview('referenceReview.otherCommentsSectionTitle')
                                        : undefined}
                                >
                                    {referenceReviewSectionVisible && (
                                        <header className="ordinary-ai-results-section__header">
                                            <strong>{tReview('referenceReview.otherCommentsSectionTitle')}</strong>
                                            <span>{tReview('referenceReview.otherCommentsSectionDescription')}</span>
                                        </header>
                                    )}
                            {globalPriorityState && (
                                <section aria-label={tReview('sidebar.priority.globalResults', { defaultValue: 'Résultats de l’analyse globale' })}>
                                    <p className="global-priority-notice">
                                        {tReview(`sidebar.priority.${globalPriorityNotice}`, {
                                            defaultValue: {
                                                final: 'Ordre de priorité pour la partie représentée',
                                                custom: 'Tri personnalisé · classement disponible via Priorité',
                                                provisional: 'Classement en cours · ordre provisoire',
                                                unavailable: 'Classement final indisponible pour cette analyse',
                                            }[globalPriorityNotice],
                                        })}
                                    </p>
                                    {directGlobalComments.map((c) => renderCommentCard(c))}
                                </section>
                            )}
                            {ordinaryComments.length > 0 && (
                                <section aria-label={globalPriorityState
                                    ? tReview('sidebar.priority.otherResults', { defaultValue: 'Autres résultats' })
                                    : undefined}>
                                    {globalPriorityState && (
                                        <header className="ordinary-ai-results-section__header">
                                            <strong>{tReview('sidebar.priority.otherResults', { defaultValue: 'Autres résultats' })}</strong>
                                            <span>{tReview('sidebar.priority.otherResultsDescription', { defaultValue: 'Classement distinct de l’analyse globale.' })}</span>
                                        </header>
                                    )}
                            {priorityComments.length > 0 ? (
                                <>
                                    <section className="priority-risk-section" aria-label={tReview('sidebar.priority.title')}>
                                        <div className="priority-risk-section__header">
                                            <span className="priority-risk-section__eyebrow">
                                                {tReview('sidebar.priority.eyebrow')}
                                            </span>
                                            <strong>{tReview('sidebar.priority.title')}</strong>
                                            <span>{priorityComments.length}</span>
                                        </div>
                                        {priorityComments.map((c) => renderCommentCard(c))}
                                    </section>

                                    {standardComments.length > 0 && (
                                        <section className="standard-risk-section" aria-label={tReview('sidebar.priority.other')}>
                                            <div className="standard-risk-section__header">
                                                <span>{tReview('sidebar.priority.other')}</span>
                                                <strong>{standardComments.length}</strong>
                                            </div>
                                            {standardComments.map((c) => renderCommentCard(c))}
                                        </section>
                                    )}
                                </>
                            ) : (
                                standardComments.map((c) => renderCommentCard(c))
                            )}
                            {toConfirmComments.length > 0 && (
                                <section className="standard-risk-section" aria-label={tReview('sidebar.priority.toConfirm', { defaultValue: 'À confirmer' })}>
                                    <div className="standard-risk-section__header">
                                        <span>{tReview('sidebar.priority.toConfirm', { defaultValue: 'À confirmer' })}</span>
                                        <strong>{toConfirmComments.length}</strong>
                                    </div>
                                    {toConfirmComments.map((c) => renderCommentCard(c))}
                                </section>
                            )}
                                </section>
                            )}
                            {renderComparisonChangeSection({
                                changes: secondaryComparisonChanges,
                                expanded: comparisonChangesExpanded,
                                onToggle: () => setComparisonChangesExpanded((prev) => !prev),
                                label: tReview('comparison.other_changes_toggle', {
                                    count: secondaryComparisonChangesCount,
                                    defaultValue: `Voir les autres changements comparés (${secondaryComparisonChangesCount})`,
                                }),
                            })}
                            {SHOW_UNCERTAIN_COMPARISON_CHANGES && renderComparisonChangeSection({
                                changes: uncertainComparisonChanges,
                                expanded: uncertainComparisonChangesExpanded,
                                onToggle: () => setUncertainComparisonChangesExpanded((prev) => !prev),
                                label: tReview('comparison.uncertain_changes_toggle', {
                                    count: uncertainComparisonChangesCount,
                                    defaultValue: `Rapprochements à confirmer (${uncertainComparisonChangesCount})`,
                                }),
                                className: 'comparison-secondary-section--muted',
                                secondaryCardClass: 'comparison-change-secondary-card--uncertain',
                            })}
                                </section>
                            )}
                        </>
                    ) : (
                        <>
                            {renderReferenceReviewSection()}
                            {!hasSecondaryComparisonResults
                                && !referenceReviewSectionVisible
                                && !referenceReviewProgressActive
                                && !referenceReviewHydrationUnavailable
                                && !referenceReviewFailed
                                && (
                                    <Empty description={emptyCommentsDescription}>
                                        {!ia_streaming && !hasCompleteAnalysisPov && (
                                            <Button
                                                type="primary"
                                                onClick={onPovRequired}
                                            >
                                                {tReview("pov_scope.selectAction")}
                                            </Button>
                                        )}
                                    </Empty>
                                )
                            }
                            {renderComparisonChangeSection({
                                changes: secondaryComparisonChanges,
                                expanded: comparisonChangesExpanded,
                                onToggle: () => setComparisonChangesExpanded((prev) => !prev),
                                label: tReview('comparison.non_major_changes_toggle', {
                                    count: secondaryComparisonChangesCount,
                                    defaultValue: `Changements sans risque majeur détecté (${secondaryComparisonChangesCount})`,
                                }),
                                className: 'comparison-secondary-section--standalone',
                            })}
                            {SHOW_UNCERTAIN_COMPARISON_CHANGES && renderComparisonChangeSection({
                                changes: uncertainComparisonChanges,
                                expanded: uncertainComparisonChangesExpanded,
                                onToggle: () => setUncertainComparisonChangesExpanded((prev) => !prev),
                                label: tReview('comparison.uncertain_changes_toggle', {
                                    count: uncertainComparisonChangesCount,
                                    defaultValue: `Rapprochements à confirmer (${uncertainComparisonChangesCount})`,
                                }),
                                className: 'comparison-secondary-section--standalone comparison-secondary-section--muted',
                                secondaryCardClass: 'comparison-change-secondary-card--uncertain',
                            })}
                        </>
                    )
                }
                    </>
                )}
                    </>
                )}
            </div>

        </>
    );
};

export default AICommentSidebar;

